import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import {
  Activity, BarChart3, TrendingUp, Shield, Zap, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, Loader, ChevronDown, Target, Layers, Eye,
  Cpu, Gauge, FileText, Database, ExternalLink, Brain, Sparkles, Wrench,
  Copy as CopyIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, LineChart, Line, AreaChart, Area, Cell, ReferenceLine,
} from 'recharts';
import PageHeader from '../components/common/PageHeader';

/**
 * WorkflowIntelligencePage — performance, hot-spot, soak, and governance
 * analytics over historical workflow runs.
 *
 * <h3>Why this exists</h3>
 * <p>Audit + canvas surface single-run truth. This page surfaces trends
 * across thousands of runs — the kind of question stress / soak / load
 * testing or capacity planning needs answered ("did P99 creep over the
 * last 24h?", "which node owns 60% of the tail?", "how often does DPG_SCAN
 * actually block in production?"). Without it, operators have to leave
 * Agentlets for an external observability tool to do percentile analytics.</p>
 *
 * <h3>Tabs</h3>
 * <ol>
 *   <li><b>Performance</b> — latency distribution histogram, percentile
 *       headlines (P50/P90/P95/P99), throughput, slowest-run sample.</li>
 *   <li><b>Hot-spots</b> — per-node P99 ranking + DAG-style table; the
 *       "what's slow inside this workflow" view.</li>
 *   <li><b>Soak / Trend</b> — bucketed time-series of throughput + P99
 *       latency over the chosen window. Spotting drift over a 24h soak
 *       test is the headline use case.</li>
 *   <li><b>Governance</b> — per-stage decision rates (PRE_GATE / DPG_SCAN /
 *       POLICY_EVAL / DECISION / EXECUTION / POST_AUDIT / POST_RECORD),
 *       run-level block / warn / clean tally, average eval time per stage.</li>
 * </ol>
 *
 * <p>Visual language matches PolicyConfig / WorkflowAudit so the Governance
 * section reads as one platform surface.</p>
 */

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM (mirrors PolicyConfig / WorkflowAudit)
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  soft: '#f8d7d4', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald:  { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:    { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:      { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:     { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:   { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
  indigo:   { bg: '#eef2ff', text: '#3730a3', border: '#c7d2fe', icon: '#6366f1' },
};

/**
 * Enterprise chart palette — deep, saturated tones that read as "production
 * data product" rather than candy-coloured demo. Brand orange is reserved
 * strictly for accents/highlights so charts feel calm, not alarming.
 *
 * Usage convention:
 *  - PRIMARY for the headline series (workflows/sec, throughput)
 *  - SECONDARY for the supporting series (tasks/sec, complementary metric)
 *  - PCT.{p50,p90,p99} for percentile lines so colour reads as "fast→slow"
 *  - GOV.{ok,warn,block} for governance decision tallies
 *  - HISTO is a 12-step ramp from cool→warm for log-bucket histograms
 *  - GRID/AXIS are calibrated against S[200]/S[500] for parity with cards
 */
const CHART = {
  primary:   '#3730a3',  // indigo-800 — workflow series
  primaryAlt: '#6366f1', // indigo-500 — workflow gradient stop
  secondary: '#0e7490',  // cyan-700 — task series (clearly "second line", non-alarming)
  secondaryAlt: '#06b6d4', // cyan-500
  accent:    BRAND.primary,  // brand orange used SPARINGLY for highlights
  axis:      S[400],
  grid:      S[150],
  pct: {
    p50: '#059669',  // emerald-600
    p90: '#d97706',  // amber-600
    p99: '#be123c',  // rose-700 — sharper than ACCENT.rose.icon
    avg: '#475569',  // slate-600
    max: '#1e293b',  // slate-900
  },
  gov: {
    ok:    '#10b981', // emerald
    warn:  '#f59e0b', // amber
    block: '#dc2626', // red-600
  },
  // 12-step cool→warm ramp for the log-scaled latency histogram so bucket
  // colour itself encodes "how slow is this bucket".
  histo: [
    '#0e7490', '#0891b2', '#06b6d4', '#22d3ee',
    '#67e8f9', '#a5f3fc', '#fde68a', '#fbbf24',
    '#f59e0b', '#ea580c', '#dc2626', '#991b1b',
  ],
};

const TABS = [
  { key: 'performance', label: 'Performance', icon: Gauge,
    desc: 'Latency distribution + P50/P90/P95/P99 percentiles + throughput per hour. The headline "is this workflow fast enough?" view.' },
  { key: 'hotspots', label: 'Hot-spots', icon: Zap,
    desc: 'Per-node P99 ranking — which steps inside the DAG own the tail latency. Pick a workflow to drill in.' },
  { key: 'soak', label: 'Soak / Trend', icon: TrendingUp,
    desc: 'Bucketed time-series of throughput and P99 latency over the chosen window. Catches drift during 24h+ stress / soak tests.' },
  { key: 'governance', label: 'Protection', icon: Shield,
    desc: 'Per-stage decision rates (DPG / Policy / Decision) and how often runs were blocked / warned / clean. The "is enforcement actually firing?" view.' },
  // Phase-12B — persisted stress / endurance / soak test runs.
  { key: 'benchmarks', label: 'Benchmarks', icon: Activity,
    desc: 'Stress / endurance / soak test history. Compare runs side-by-side, export PDF/XLSX reports, track production-readiness over time.' },
  // Phase-3 — LLM-powered analysis over the same data the dashboards render.
  // Strictly on-demand: no LLM calls happen until the operator clicks
  // "Analyze". Output is structured JSON (summary + findings + recs +
  // comparisons) so the UI renders deterministically.
  { key: 'ai-analyst', label: 'AI Analyst', icon: Brain,
    desc: 'Claude-powered performance review of this window — severity-ranked findings, prescriptive tuning recommendations citing exact Spring properties, and benchmark comparisons. On-demand only; cached briefly to avoid re-billing identical analyses.' },
];

const WINDOW_OPTIONS = [
  { id: '1h',  label: 'Last 1 hour',   hours: 1 },
  { id: '4h',  label: 'Last 4 hours',  hours: 4 },
  { id: '8h',  label: 'Last 8 hours',  hours: 8 },
  { id: '12h', label: 'Last 12 hours', hours: 12 },
  { id: '24h', label: 'Last 24 hours', hours: 24 },
  { id: '7d',  label: 'Last 7 days',   hours: 24 * 7 },
  { id: '30d', label: 'Last 30 days',  hours: 24 * 30 },
];

/* ─── Shared base styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

const GlobalStyles = () => (
  <style>{`
    @keyframes wipSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes wipFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes wipShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    .wip-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .wip-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; }
    .wip-stat-card { transition: all 0.2s ease !important; }
    .wip-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .wip-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .wip-row:hover { background: ${S[25]} !important; }
    .wip-btn-ghost:hover { background: ${S[100]} !important; }
  `}</style>
);

export default function WorkflowIntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Filters ─────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'performance');
  const [windowId, setWindowId] = useState(searchParams.get('window') || '7d');
  const [workflowId, setWorkflowId] = useState(searchParams.get('workflowId') || null);
  // Run filter — when a specific benchmark run is picked from the
  // dropdown, every Performance tab query is scoped to THAT run's
  // wall-clock window instead of the rolling Window selector. Set to
  // null when the operator wants the cumulative/aggregate view.
  // (perf-debug 2026-05-02 #137)
  const [runId, setRunId] = useState(searchParams.get('runId') || null);
  // Cache of the last 10 completed/aborted/failed benchmark runs so the
  // dropdown stays populated without paginating the user through the
  // full Benchmarks tab. Refreshed on page mount and on Refresh.
  const [recentRuns, setRecentRuns] = useState([]);

  // Time bounds — when a run is selected they snap to that run's
  // [startedAt, completedAt+60s]. Otherwise derived from the rolling
  // window selector. The +60s tail catches workflows that started in
  // the harness window but completed shortly after the harness stopped
  // firing (matches the convention in BenchmarkService.getServerMetrics).
  const selectedRun = useMemo(
    () => recentRuns.find((r) => r.id === runId) || null,
    [recentRuns, runId]
  );
  const { fromInstant, toInstant } = useMemo(() => {
    if (selectedRun && selectedRun.startedAt) {
      const start = new Date(selectedRun.startedAt).toISOString();
      // For runs still RUNNING, leave `to` open so the query goes up to "now".
      const end = selectedRun.completedAt
        ? new Date(new Date(selectedRun.completedAt).getTime() + 60_000).toISOString()
        : null;
      return { fromInstant: start, toInstant: end };
    }
    // Fallback to the same default the URL sync uses ('7d') so an
    // unknown windowId reverts to the platform default rather than
    // surprising the operator with whatever happens to be at index N.
    const opt = WINDOW_OPTIONS.find((w) => w.id === windowId)
              || WINDOW_OPTIONS.find((w) => w.id === '7d');
    return {
      fromInstant: new Date(Date.now() - opt.hours * 60 * 60 * 1000).toISOString(),
      toInstant: null,
    };
  }, [windowId, selectedRun]);

  // Bucket size auto-scales with the window so the chart stays readable
  // (about 50–100 points per chart). Backend `parseBucket` only honours
  // 1m / 5m / 15m / 1h / 1d, so we map every window onto one of those.
  //   1h  → 1m  (60 points)
  //   4h  → 5m  (48 points)
  //   8h  → 5m  (96 points)
  //   12h → 15m (48 points)
  //   24h → 15m (96 points)
  //   7d  → 1h  (168 points)
  //   30d → 1d  (30 points)
  const bucketSize = useMemo(() => {
    if (windowId === '1h')                          return '1m';
    if (windowId === '4h' || windowId === '8h')     return '5m';
    if (windowId === '12h' || windowId === '24h')   return '15m';
    if (windowId === '7d')                          return '1h';
    return '1d';
  }, [windowId]);

  // ── Data ─────────────────────────────────────────────────────────────
  const [workflows, setWorkflows] = useState([]);
  const [perfData, setPerfData] = useState(null);
  const [hotspotData, setHotspotData] = useState(null);
  const [seriesData, setSeriesData] = useState(null);
  const [govData, setGovData] = useState(null);
  // AI Analyst is on-demand (LLM costs money) — never auto-loaded.
  // aiData null = "not yet asked"; non-null = the latest envelope; the
  // AIAnalystView component renders the prompt-to-analyze CTA when null.
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Stamp the wall-clock at every successful load so the UI can show
  // "refreshed 12s ago" — at high-frequency runtime the difference between
  // "5 seconds stale" and "5 minutes stale" is the entire story.
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  // Tick every second so the "Xs ago" indicator stays current without
  // refetching the data; cheap and avoids stale-feeling UIs.
  const [, setNowTick] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // ── Loaders (one per tab, fired only when the tab activates) ───────
  const loadWorkflows = useCallback(async () => {
    try {
      const res = await api.getWorkflowIntelligenceWorkflows({ from: fromInstant });
      if (res.ok) setWorkflows(Array.isArray(res.data) ? res.data : []);
    } catch (_) { /* picker is optional */ }
  }, [fromInstant]);

  // apiFetch returns { ok:true, data } on success; on failure it returns
  // { ok:false, error: <message> } (network/HTTP errors are caught and
  // normalised). Callers should read `res.error` first — checking only
  // `res.data?.message` swallows the real failure and replaces it with a
  // bland fallback string.
  const apiErrMsg = (res, fallback) =>
    res?.error || res?.data?.message || res?.data?.error || fallback;

  const loadPerformance = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `to` is included only when the Run dropdown has scoped us to a
      // specific benchmark run (perf-debug #137). For aggregate window
      // views, `to` stays absent and the backend defaults to now().
      const opts = { workflowId, from: fromInstant, slowSampleN: 10 };
      if (toInstant) opts.to = toInstant;
      const res = await api.getWorkflowIntelligencePerformance(opts);
      if (res.ok) { setPerfData(res.data); setLastRefreshedAt(new Date()); }
      else setError(apiErrMsg(res, 'Failed to load'));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workflowId, fromInstant, toInstant]);

  // Load the last 10 benchmark runs once for the Run dropdown. The
  // listBenchmarks endpoint is already capped at 200 server-side; we
  // slice to 10 most-recent here so the dropdown stays scannable.
  // Refreshed on Refresh so a run completed in another tab shows up.
  const loadRecentRuns = useCallback(async () => {
    try {
      const res = await api.listBenchmarks();
      if (res.ok && Array.isArray(res.data)) {
        const sorted = [...res.data].sort((a, b) => {
          const ta = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const tb = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return tb - ta;
        });
        setRecentRuns(sorted.slice(0, 10));
      }
    } catch (e) {
      // Best-effort — empty dropdown is fine if the API is down.
    }
  }, []);

  const loadHotspots = useCallback(async () => {
    if (!workflowId) { setHotspotData(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await api.getWorkflowIntelligenceNodeHotspots({
        workflowId, from: fromInstant, topN: 30,
      });
      if (res.ok) { setHotspotData(res.data); setLastRefreshedAt(new Date()); }
      else setError(apiErrMsg(res, 'Failed to load'));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workflowId, fromInstant]);

  const loadSeries = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getWorkflowIntelligenceTimeSeries({
        workflowId, from: fromInstant, bucketSize,
      });
      if (res.ok) { setSeriesData(res.data); setLastRefreshedAt(new Date()); }
      else setError(apiErrMsg(res, 'Failed to load'));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workflowId, fromInstant, bucketSize]);

  const loadGovernance = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getWorkflowIntelligenceGovernance({
        workflowId, from: fromInstant,
      });
      if (res.ok) { setGovData(res.data); setLastRefreshedAt(new Date()); }
      else setError(apiErrMsg(res, 'Failed to load'));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workflowId, fromInstant]);

  /**
   * AI analysis — strictly on-demand. The button on the tab triggers this.
   * `force=true` bypasses the server-side cache so an operator who just
   * tweaked Temporal OSS config gets a fresh read instead of a stale-but-cached
   * verdict. Error handling is local (aiError) so it doesn't blow away
   * the dashboard's `error` state.
   */
  const loadAiAnalysis = useCallback(async ({ force = false } = {}) => {
    setAiLoading(true); setAiError(null);
    try {
      const res = await api.getWorkflowIntelligenceAIAnalysis({
        workflowId, from: fromInstant, bucketSize, force,
      });
      if (res.ok) { setAiData(res.data); setLastRefreshedAt(new Date()); }
      else {
        // Translate the AbortController's terse "signal is aborted without
        // reason" into something operator-actionable. Anything else passes
        // through verbatim.
        const raw = apiErrMsg(res, 'Failed to generate analysis');
        const friendly = /signal is aborted|abort/i.test(raw)
          ? 'Request timed out before Claude finished. Try a narrower window (Last 1 hour) or check the backend `llm.timeout-seconds` setting.'
          : raw;
        setAiError(friendly);
      }
    } catch (e) { setAiError(e.message); }
    finally { setAiLoading(false); }
  }, [workflowId, fromInstant, bucketSize]);

  // When the operator changes the workflow / window filter, the previous
  // analysis is stale (it described a different slice of data). Drop it so
  // they're re-prompted to click Analyze rather than reading old advice.
  useEffect(() => { setAiData(null); setAiError(null); }, [workflowId, fromInstant, bucketSize]);

  // Load workflow picker once, then per-tab data on tab/filter change.
  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);
  // Recent benchmark runs feed the Run dropdown (#137). Loaded once
  // on mount so the picker is populated even before the operator
  // visits the Benchmarks tab.
  useEffect(() => { loadRecentRuns(); }, [loadRecentRuns]);
  useEffect(() => {
    if (activeTab === 'performance') loadPerformance();
    else if (activeTab === 'hotspots') loadHotspots();
    else if (activeTab === 'soak') loadSeries();
    else if (activeTab === 'governance') loadGovernance();
  }, [activeTab, loadPerformance, loadHotspots, loadSeries, loadGovernance]);

  // URL sync for deep links.
  useEffect(() => {
    const next = new URLSearchParams();
    if (activeTab && activeTab !== 'performance') next.set('tab', activeTab);
    if (windowId && windowId !== '7d') next.set('window', windowId);
    if (workflowId) next.set('workflowId', workflowId);
    if (runId) next.set('runId', runId);
    setSearchParams(next, { replace: true });
  }, [activeTab, windowId, workflowId, runId, setSearchParams]);

  const reload = () => {
    // Pick up any new benchmark runs created since the last refresh
    // so the Run dropdown stays current.
    loadRecentRuns();
    if (activeTab === 'performance') loadPerformance();
    else if (activeTab === 'hotspots') loadHotspots();
    else if (activeTab === 'soak') loadSeries();
    else if (activeTab === 'governance') loadGovernance();
    // AI Analyst on the global Refresh button = force a fresh LLM run only
    // if there's already an analysis on screen. We never auto-trigger the
    // first call from the global Refresh — that's what the in-tab Analyze
    // button is for.
    else if (activeTab === 'ai-analyst' && aiData) loadAiAnalysis({ force: true });
  };

  const activeTabMeta = TABS.find((t) => t.key === activeTab) || TABS[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: S[50] }}>
      <GlobalStyles />

      {/* ═══ HEADER ════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${S[200]}`, flexShrink: 0 }}>
        <div style={{ padding: '22px 28px 0' }}>
          <PageHeader
            icon={BarChart3}
            title="Workflow Intelligence"
            subtitle="Performance + protection analytics across thousands of runs — percentiles, hot-spots, drift, decision rates"
            actions={
              <>
                {/* Stamp the wall-clock at every successful load so the operator
                    can tell at a glance whether a "good number" is fresh or
                    minutes-stale. */}
                <span title="Server-side aggregation. Click Refresh to force a re-query."
                  style={{ fontSize: 11, color: lastRefreshedAt ? S[500] : S[400],
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                  {lastRefreshedAt
                    ? <>refreshed <span style={{ color: S[700], fontWeight: 600 }}>
                        {fmtTimeAgo(lastRefreshedAt)}</span></>
                    : 'not yet loaded'}
                </span>
                <button className="wip-btn-ghost" onClick={reload} disabled={loading}
                  title="Force re-query — bypass any client-side state and refetch from the API"
                  style={{
                    ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
                    background: BRAND.primary, color: '#fff', border: 'none',
                    boxShadow: '0 1px 3px rgba(226,74,58,0.25)',
                  }}>
                  <RefreshCw size={13} style={{ animation: loading ? 'wipSpin 1s linear infinite' : 'none' }} />
                  {loading ? 'Refreshing…' : 'Refresh'}
                </button>
              </>
            }
          />
        </div>

        {/* Tab nav */}
        <div style={{ display: 'flex', gap: 0, marginTop: 16, padding: '0 20px' }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key}
                className={isActive ? '' : 'wip-tab'}
                onClick={() => setActiveTab(tab.key)}
                title={tab.desc}
                style={{
                  flex: 1, padding: '14px 16px',
                  borderBottom: isActive ? `3px solid ${BRAND.primary}` : '3px solid transparent',
                  background: 'transparent', border: 'none', borderBottomStyle: 'solid',
                  cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 500,
                  color: isActive ? BRAND.primary : S[500],
                  transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center',
                  whiteSpace: 'nowrap',
                }}>
                <Icon size={15} strokeWidth={isActive ? 2.2 : 1.8} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab description bar */}
      <div style={{
        padding: '8px 24px', background: BRAND.gradientSubtle,
        borderBottom: `1px solid ${S[150]}`,
        fontSize: 11, color: S[500], fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
      }}>
        <activeTabMeta.icon size={12} color={S[400]} />
        {activeTabMeta.desc}
      </div>

      {/* ═══ MAIN BODY ═════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 32px' }}>

        {/* Filter strip — the Workflow selector is hidden on the Benchmarks
            tab because benchmark runs are tenant-level (a stress run isn't
            scoped to a specific workflow); showing it there was misleading
            because the selection had no effect on the run list. */}
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: '12px 14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          gap: 14, marginBottom: 16,
        }}>
          {/* Workflow picker — only on tabs where a workflow scope is meaningful. */}
          {activeTab !== 'benchmarks' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
                  textTransform: 'uppercase', letterSpacing: '0.06em' }}>Workflow</span>
                <select value={workflowId || ''}
                  onChange={(e) => setWorkflowId(e.target.value || null)}
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    fontSize: 11, color: S[700], cursor: 'pointer', background: '#fff',
                    fontFamily: 'inherit', minWidth: 220,
                  }}>
                  <option value="">All workflows (tenant)</option>
                  {workflows.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} {w.totalRunCount ? `(${w.totalRunCount} runs)` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ width: 1, height: 24, background: S[200] }} />
            </>
          )}

          {/* Time window — applies to every tab including Benchmarks (filters
              the benchmark list by startedAt within the chosen window).
              When the Run dropdown below has a specific run selected, this
              picker is disabled because the time bounds are then derived
              from that run's [startedAt, completedAt+60s] instead. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>Window</span>
            <select value={windowId} onChange={(e) => setWindowId(e.target.value)}
              disabled={!!runId && activeTab === 'performance'}
              title={runId && activeTab === 'performance'
                ? 'Disabled: window is fixed to the selected run\'s wall-clock range'
                : 'Rolling time window for the dashboard'}
              style={{
                padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                fontSize: 11, color: (runId && activeTab === 'performance') ? S[400] : S[700],
                cursor: (runId && activeTab === 'performance') ? 'not-allowed' : 'pointer',
                background: (runId && activeTab === 'performance') ? S[50] : '#fff',
                fontFamily: 'inherit',
              }}>
              {WINDOW_OPTIONS.map((w) => (
                <option key={w.id} value={w.id}>{w.label}</option>
              ))}
            </select>
          </div>

          {/* Run picker — Performance tab only. Lets the operator scope
              the dashboard to the wall-clock window of a specific recent
              benchmark/stress run instead of looking at the cumulative
              hourly aggregate. List capped at the 10 most-recent runs
              so it stays scannable. (perf-debug 2026-05-02 #137) */}
          {activeTab === 'performance' && (
            <>
              <div style={{ width: 1, height: 24, background: S[200] }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: S[500],
                  textTransform: 'uppercase', letterSpacing: '0.06em' }}>Run</span>
                <select value={runId || ''}
                  onChange={(e) => setRunId(e.target.value || null)}
                  title="Scope the dashboard to a specific benchmark run's wall-clock window"
                  style={{
                    padding: '6px 10px', borderRadius: 6, border: `1px solid ${S[200]}`,
                    fontSize: 11, color: S[700], cursor: 'pointer', background: '#fff',
                    fontFamily: 'inherit', minWidth: 280,
                  }}>
                  <option value="">All runs (use Window)</option>
                  {recentRuns.map((r) => {
                    // Compose a label that's scannable at a glance:
                    //   "v2 native curated · 5/2 13:25 · 4.4 min · 5.2k fired"
                    // Time formatting follows the rest of the page (locale).
                    const start = r.startedAt ? new Date(r.startedAt) : null;
                    const startLbl = start
                      ? `${start.getMonth() + 1}/${start.getDate()} `
                          + `${start.getHours().toString().padStart(2, '0')}:`
                          + `${start.getMinutes().toString().padStart(2, '0')}`
                      : '?';
                    const dur = r.durationSec != null
                      ? `${(Number(r.durationSec) / 60).toFixed(1)} min`
                      : '';
                    const fired = r.aggregateMetrics
                              && r.aggregateMetrics.totals
                              && r.aggregateMetrics.totals.fired
                      ? `${(Number(r.aggregateMetrics.totals.fired) / 1000).toFixed(1)}k fired`
                      : '';
                    const status = (r.status || '').toLowerCase();
                    const statusBadge = status && status !== 'completed'
                      ? ` (${status.toUpperCase()})` : '';
                    const parts = [r.name || 'unnamed', startLbl, dur, fired]
                      .filter(Boolean).join(' · ');
                    return (
                      <option key={r.id} value={r.id}>
                        {parts}{statusBadge}
                      </option>
                    );
                  })}
                </select>
                {runId && (
                  <button
                    onClick={() => setRunId(null)}
                    title="Clear run filter and return to the rolling Window view"
                    style={{
                      fontSize: 10, padding: '3px 8px', borderRadius: 4,
                      border: `1px solid ${S[200]}`, background: '#fff', color: S[600],
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>Clear</button>
                )}
              </div>
            </>
          )}

          {/* Selected-run callout — confirms the current view is
              scoped to a specific run's window so the operator
              isn't confused that the Window picker did nothing. */}
          {runId && selectedRun && activeTab === 'performance' && (
            <span style={{ marginLeft: 'auto', fontSize: 11,
              padding: '4px 10px', background: '#0d4f3c12', color: '#0d4f3c',
              border: `1px solid #0d4f3c40`, borderRadius: 6 }}>
              <strong>Run-scoped view:</strong> {selectedRun.name}
              {selectedRun.durationSec != null
                ? ` · ${(Number(selectedRun.durationSec) / 60).toFixed(1)} min`
                : ''}
              {selectedRun.completedAt
                ? ` · ${new Date(selectedRun.completedAt).toLocaleTimeString()}`
                : ' · running'}
            </span>
          )}

          {activeTab === 'soak' && (
            <>
              <div style={{ width: 1, height: 24, background: S[200] }} />
              <span style={{ fontSize: 11, color: S[500] }}>
                bucket: <code style={{ background: S[100], padding: '1px 6px', borderRadius: 4,
                  fontFamily: "'IBM Plex Mono', monospace" }}>{bucketSize}</code>
              </span>
            </>
          )}

          {activeTab === 'benchmarks' && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: S[500] }}>
              Showing benchmark runs started within window
            </span>
          )}

          {activeTab === 'hotspots' && !workflowId && (
            <span style={{ marginLeft: 'auto', fontSize: 11, color: ACCENT.amber.text,
              padding: '4px 10px', background: ACCENT.amber.bg,
              border: `1px solid ${ACCENT.amber.border}`, borderRadius: 6 }}>
              Pick a workflow to see node hot-spots
            </span>
          )}
        </div>

        {/* Body states */}
        {loading && <BodyLoading />}
        {!loading && error && <BodyError message={error} />}

        {/* ─── PERFORMANCE TAB ─── */}
        {!loading && !error && activeTab === 'performance' && perfData && (
          <PerformanceView data={perfData} onPickRun={(rid, wfId) => navigate(`/workflow-audit?workflowId=${wfId}`)} />
        )}

        {/* ─── HOT-SPOTS TAB ─── */}
        {!loading && !error && activeTab === 'hotspots' && (
          workflowId
            ? hotspotData ? <HotspotsView data={hotspotData} /> : <BodyEmpty msg="No node executions in this window." />
            : <BodyEmpty msg="Pick a workflow above to see per-node hot-spots." />
        )}

        {/* ─── SOAK / TREND TAB ─── */}
        {!loading && !error && activeTab === 'soak' && seriesData && (
          <SoakView data={seriesData} bucketSize={bucketSize} />
        )}

        {/* ─── GOVERNANCE TAB ─── */}
        {!loading && !error && activeTab === 'governance' && govData && (
          <GovernanceView data={govData} />
        )}

        {/* ─── BENCHMARKS TAB (Phase-12B) ─── */}
        {activeTab === 'benchmarks' && <BenchmarksView fromInstant={fromInstant} windowLabel={WINDOW_OPTIONS.find(w => w.id === windowId)?.label} />}

        {/* ─── AI ANALYST TAB (Phase-3) ───
            On-demand only — render the CTA + analysis without using the
            shared `loading` / `error` state, so toggling tabs doesn't blank
            the dashboard's last good data. */}
        {activeTab === 'ai-analyst' && (
          <AIAnalystView
            data={aiData}
            loading={aiLoading}
            error={aiError}
            workflowId={workflowId}
            workflowName={(workflows.find((w) => w.id === workflowId)?.name) || null}
            windowLabel={WINDOW_OPTIONS.find((w) => w.id === windowId)?.label}
            onAnalyze={() => loadAiAnalysis({ force: false })}
            onRefresh={() => loadAiAnalysis({ force: true })}
          />
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   PERFORMANCE VIEW — percentile headline cards + latency histogram + slowest runs
   ══════════════════════════════════════════════════════════════════════════════ */

function PerformanceView({ data, onPickRun }) {
  const p = data.percentiles || {};
  const tm = data.taskMetrics || {};
  const tp = tm.taskDurationMs || {};
  const tpw = tm.tasksPerWorkflow || {};
  const histogram = (data.distribution || []).map((b, i) => ({
    label: histLabel(b),
    count: b.count,
    fill: histFill(i, data.distribution.length),
  }));
  const histMaxMs = Math.max(...(data.distribution || []).map((b) => b.bucketHighMs || 0), 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Headline: throughput rates first (the question operators ask first),
          then the latency tail. Two rows because cramming 7 cards into one
          row produces a wall of numbers nobody reads. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Workflows / sec" value={fmtPerSec(data.workflowsPerSec)}
          accent={ACCENT.indigo} icon={Zap}
          subtitle={`${fmtN(data.runCount)} runs · ${fmtN(data.inFlightCount || 0)} in-flight`} />
        <StatCard label="Tasks / sec" value={fmtPerSec(data.tasksPerSec)}
          accent={ACCENT.sky} icon={Activity}
          subtitle={`${fmtN(tm.totalInvocations || 0)} task runs · ${fmtN(tm.uniqueNodeIds || 0)} distinct nodes`} />
        <StatCard label="P99 Workflow Latency" value={fmtMs(p.p99)}
          accent={ACCENT.rose} icon={AlertTriangle}
          subtitle={`P50 ${fmtMs(p.p50)} · P95 ${fmtMs(p.p95)}`} />
        <StatCard label="P99 Task Latency" value={fmtMs(tp.p99)}
          accent={ACCENT.amber} icon={Gauge}
          subtitle={tp.p50 != null ? `P50 ${fmtMs(tp.p50)} · max ${fmtMs(tp.max)}` : 'No task data in window'} />
        <StatCard label="Tasks / Workflow" value={tpw.avg ? tpw.avg.toFixed(1) : '—'}
          accent={ACCENT.violet} icon={Layers}
          subtitle={tpw.p99 ? `P99 ${tpw.p99} · max ${tpw.max}` : ''} />
      </div>

      {/* Secondary row: success/failure breakdown + sustained-tail-pressure throughput */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Success rate"
          value={data.successRate != null ? `${(data.successRate * 100).toFixed(2)}%` : '—'}
          accent={ACCENT.emerald} icon={CheckCircle}
          subtitle={`${fmtN(data.successCount || 0)} ok / ${fmtN(data.failedCount || 0)} failed`} />
        <StatCard label="Burst ceiling"
          value={tm.p99TaskBurstPerSec ? `${Math.round(tm.p99TaskBurstPerSec)}/s` : '—'}
          accent={ACCENT.slate} icon={TrendingUp}
          subtitle="P99 task starts in any 1-second slice" />
        <StatCard label="Effective throughput @ P99"
          value={fmtPerSec(data.p99EffectiveThroughputPerSec)}
          accent={ACCENT.indigo} icon={Gauge}
          subtitle="Sustained workflow×task rate under tail-latency pressure" />
        <StatCard label="Throughput / hr"
          value={`${(data.throughputPerHour || 0).toFixed(1)}/hr`}
          accent={ACCENT.slate} icon={Activity} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Histogram */}
        <Panel title="Workflow latency distribution"
          subtitle="12 log-scaled buckets · cool-to-warm = fast-to-slow tail">
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={histogram} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: CHART.axis }}
                  interval={0} angle={-22} textAnchor="end" height={56} />
                <YAxis tick={{ fontSize: 10, fill: CHART.axis }} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: `${CHART.primary}10` }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {histogram.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Mini-legend so the colour ramp is self-explanatory. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10,
            color: S[500], padding: '4px 12px 8px' }}>
            <span>fast</span>
            <div style={{ flex: 1, height: 6, borderRadius: 3,
              background: `linear-gradient(90deg, ${CHART.histo.join(', ')})` }} />
            <span>slow</span>
          </div>
        </Panel>

        {/* Percentile breakdown */}
        <Panel title="Percentile breakdown" subtitle={`avg ${fmtMs(p.avg)} · max ${fmtMs(p.max)}`}>
          <PercentileBars p={p} taskP={tp} />
        </Panel>
      </div>

      {/* Slowest runs table */}
      <Panel title="Slowest runs" subtitle="Click a row to drill into its enforcement timeline">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px,2fr) 110px 110px minmax(180px,1.5fr)', fontSize: 12 }}>
          <TCell hdr>Run ID</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Duration</TCell>
          <TCell hdr style={{ textAlign: 'center' }}>Status</TCell>
          <TCell hdr>Started</TCell>
          {(data.topSlowRuns || []).map((r) => (
            <React.Fragment key={r.runId}>
              <TCell onClick={() => onPickRun && onPickRun(r.runId, r.workflowId)} style={{ cursor: 'pointer' }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: S[800] }}>
                  {String(r.runId).slice(0, 12)}
                </span>
              </TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: S[800] }}>
                {fmtMs(r.durationMs)}
              </TCell>
              <TCell style={{ textAlign: 'center' }}>
                <StatusPill status={r.status} />
              </TCell>
              <TCell style={{ color: S[600] }}>
                {r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}
              </TCell>
            </React.Fragment>
          ))}
        </div>
        {(data.topSlowRuns || []).length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: S[500], fontSize: 12 }}>
            No completed runs in this window.
          </div>
        )}
      </Panel>
    </div>
  );
}

function PercentileBars({ p, taskP }) {
  // Show side-by-side workflow vs task percentile bars when task data is
  // available — instantly tells the operator whether the tail belongs to
  // the orchestration layer or the work each step is doing.
  const haveTask = taskP && (taskP.p50 != null || taskP.p99 != null);
  const max = Math.max(p.p50 || 0, p.p90 || 0, p.p95 || 0, p.p99 || 0, p.max || 0,
    haveTask ? (taskP.p50 || 0) : 0, haveTask ? (taskP.p99 || 0) : 0, 1);
  const rows = [
    { label: 'P50', wf: p.p50,  task: taskP?.p50,  color: CHART.pct.p50 },
    { label: 'P90', wf: p.p90,  task: taskP?.p90,  color: CHART.pct.p90 },
    { label: 'P95', wf: p.p95,  task: taskP?.p95,  color: CHART.pct.p90 },
    { label: 'P99', wf: p.p99,  task: taskP?.p99,  color: CHART.pct.p99 },
    { label: 'Max', wf: p.max,  task: taskP?.max,  color: CHART.pct.max },
  ];
  return (
    <div style={{ padding: 12 }}>
      {haveTask && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 10,
          color: S[500], marginBottom: 10, fontWeight: 600 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 6, borderRadius: 2, background: CHART.primary }} />
            workflow
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 16, height: 6, borderRadius: 2, background: CHART.secondary }} />
            task
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 36, fontSize: 11, fontWeight: 700, color: r.color,
              fontFamily: "'IBM Plex Mono', monospace" }}>
              {r.label}
            </span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Workflow bar */}
              <div style={{ height: haveTask ? 12 : 22, background: S[100],
                borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  width: `${((r.wf || 0) / max) * 100}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${CHART.primary}90, ${CHART.primary})`,
                  borderRadius: 4, transition: 'width 0.4s ease',
                }} />
              </div>
              {haveTask && (
                <div style={{ height: 12, background: S[100], borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                  <div style={{
                    width: `${((r.task || 0) / max) * 100}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${CHART.secondary}90, ${CHART.secondary})`,
                    borderRadius: 4, transition: 'width 0.4s ease',
                  }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2,
              minWidth: 80, textAlign: 'right' }}>
              <span style={{ fontSize: 11, color: S[800], fontVariantNumeric: 'tabular-nums',
                fontFamily: "'IBM Plex Mono', monospace" }}>
                {fmtMs(r.wf)}
              </span>
              {haveTask && (
                <span style={{ fontSize: 10, color: S[500], fontVariantNumeric: 'tabular-nums',
                  fontFamily: "'IBM Plex Mono', monospace" }}>
                  {fmtMs(r.task)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   HOT-SPOTS VIEW — per-node P99 ranking
   ══════════════════════════════════════════════════════════════════════════════ */

function HotspotsView({ data }) {
  const nodes = data.nodes || [];
  const maxP99 = Math.max(...nodes.map((n) => n.percentiles?.p99 || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Runs analyzed" value={fmtN(data.runCount)} accent={ACCENT.slate} icon={Activity} />
        <StatCard label="Distinct nodes" value={fmtN(data.nodeCount)} accent={ACCENT.violet} icon={Cpu} />
        <StatCard label="Slowest node" value={nodes[0]?.nodeId?.slice(0, 14) || '—'}
          accent={ACCENT.rose} icon={AlertTriangle}
          subtitle={nodes[0] ? `${fmtMs(nodes[0].percentiles?.p99)} P99` : ''} />
      </div>

      <Panel title="Node hot-spot ranking" subtitle="Sorted by P99 latency · longest bar = biggest tail-latency contributor">
        <div style={{ padding: 0 }}>
          {nodes.map((n) => {
            const p99 = n.percentiles?.p99 || 0;
            const errorRate = n.errorRate || 0;
            const errorAccent = errorRate > 0.05 ? ACCENT.rose
                              : errorRate > 0 ? ACCENT.amber
                              : ACCENT.emerald;
            return (
              <div key={n.nodeId} className="wip-row" style={{
                padding: '12px 16px', borderBottom: `1px solid ${S[100]}`,
                display: 'grid', gridTemplateColumns: 'minmax(180px,1.5fr) 1fr 110px 110px 90px',
                alignItems: 'center', gap: 12, fontSize: 12,
              }}>
                <div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace",
                    fontWeight: 600, color: S[900],
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.nodeId}
                  </div>
                  <div style={{ fontSize: 10, color: S[500], marginTop: 2,
                    fontFamily: "'IBM Plex Mono', monospace" }}>
                    {n.nodeType}
                  </div>
                </div>
                <div style={{ height: 16, background: S[100], borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
                  <div style={{
                    width: `${(p99 / maxP99) * 100}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${ACCENT.amber.icon}70, ${ACCENT.rose.icon})`,
                    borderRadius: 4,
                  }} />
                </div>
                <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  fontFamily: "'IBM Plex Mono', monospace", color: S[800] }}>
                  P99 {fmtMs(p99)}
                </div>
                <div style={{ textAlign: 'right', fontSize: 11, color: S[500] }}>
                  {fmtN(n.invocations)} runs
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                    background: errorAccent.bg, border: `1px solid ${errorAccent.border}`,
                    color: errorAccent.text, fontFamily: "'IBM Plex Mono', monospace",
                  }}>
                    {((errorRate) * 100).toFixed(1)}% err
                  </span>
                </div>
              </div>
            );
          })}
          {nodes.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: S[500], fontSize: 12 }}>
              No node-level data in this window. Run the workflow to populate the index.
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SOAK / TREND VIEW — bucketed throughput + P99 over time
   ══════════════════════════════════════════════════════════════════════════════ */

/**
 * GLOSSARY — concrete, prescriptive tooltips on every metric card. We keep
 * the wording at "operator who's tuning a production cluster" level, not
 * "marketing copy", so the tuning advice is actionable. Whenever the
 * advice references a specific Spring property name, that property is the
 * authoritative knob — see TemporalHealthService.workerConfigSection().
 */
const SOAK_GLOSSARY = {
  buckets:
    'A bucket is one time-window slice the chart is divided into. Bucket ' +
    'size auto-scales with the selected window: 1h→1m, 24h→15m, 7d→1h, ' +
    '30d→1d. Each chart point aggregates every task that landed inside its ' +
    'bucket — bucketed by created_at for "dispatched", started_at for ' +
    '"started", and completed_at for "completed".',
  totalRuns:
    'Number of workflow runs that started in this window. ' +
    'avg shows the realised workflow throughput across the entire window.',
  totalTasks:
    'Number of activity executions that started in this window. ' +
    'Per-workflow fan-out = totalTasks / totalRuns.',
  totalErrors:
    'Workflow runs that ended in failed/terminated/timed-out state in this ' +
    'window. Sustained error rate > 1% in production = page someone.',
  dispatched:
    'Tasks created by the DAG executor — the demand signal. If dispatched/sec ' +
    'consistently exceeds started/sec, the queue is growing. Two responses: ' +
    'widen the worker pool (raise temporal.worker.max-concurrent-activity-' +
    'executions or add poll threads via temporal.worker.activity-poll-thread-' +
    'count), or scale horizontally with more worker replicas.',
  started:
    'Tasks picked up by a worker — realised throughput. The hard ceiling is ' +
    'temporal.worker.max-concurrent-activity-executions × worker-replicas. ' +
    'Started < dispatched and queue growing = workers undersized. Started < ' +
    'dispatched but queue stable = healthy steady-state at saturation.',
  completed:
    'Tasks finished — drain rate. completed/sec lagging dispatched/sec for ' +
    'minutes = in-flight work is accumulating; sustained = expect the worker ' +
    'pool to OOM under continued load.',
  pending:
    'Tasks queued by the DAG executor but not yet picked up by a worker. ' +
    'Healthy production stays near zero. Pending > 1k for more than a couple ' +
    'of minutes means the worker pool is starved — raise temporal.worker.max-' +
    'concurrent-activity-executions, raise temporal.worker.activity-poll-' +
    'thread-count, or add worker replicas. Watch avg queue-wait alongside — ' +
    'when it climbs above 1s the queue is biting end-users.',
  active:
    'Tasks currently running on a worker. When this saturates the capacity ' +
    'ceiling (max-concurrent-activity-executions × replicas), the worker ' +
    'pool is fully utilised — every additional dispatch lands in pending. ' +
    'Plan capacity from peak active, not average.',
  queueWait:
    'Average time a task sat queued before a worker picked it up, across ' +
    'this window. Healthy production stays under 1 second. Sustained > 5s = ' +
    'worker pool undersized for the dispatch rate. Tune by raising temporal.' +
    'worker.max-concurrent-activity-executions or adding replicas.',
  runMs:
    'Average activity execution time, from start to complete (excludes ' +
    'queue wait). If run-time creeps up while throughput stays flat, the ' +
    'bottleneck is downstream of Temporal OSS — slow LLM, slow JQ, slow DB ' +
    'write. Use the Hot-spots tab to find the per-node breakdown.',
  drainRate:
    'completed/sec averaged across buckets where dispatched/sec was zero — ' +
    'i.e. the speed at which the worker pool clears a backlog once new ' +
    'demand stops. If drain rate is way below started/sec under load, your ' +
    'workers are also being limited by something other than concurrency ' +
    '(LLM rate-limit, slow DB, GC pauses).',
};

function SoakView({ data, bucketSize }) {
  const bucketSeconds = data.bucketSeconds || 60;
  const series = (data.buckets || []).map((b) => ({
    bucket: shortTime(b.bucketStart),
    runs: b.runCount,
    workflowsPerSec: b.workflowsPerSec,
    tasks: b.taskCount,
    // Legacy alias kept for any older consumer; equal to tasksStartedPerSec.
    tasksPerSec: b.tasksPerSec,
    // Phase-3 queue/saturation fields (default to 0 to keep recharts happy
    // when older buckets are missing the field — recharts skips nulls but
    // some chart configurations infer the domain from the first row).
    tasksDispatchedPerSec: b.tasksDispatchedPerSec || 0,
    tasksStartedPerSec:    b.tasksStartedPerSec    || 0,
    tasksCompletedPerSec:  b.tasksCompletedPerSec  || 0,
    tasksPending:          b.tasksPending          || 0,
    tasksActive:           b.tasksActive           || 0,
    avgQueueWaitMs:        b.avgQueueWaitMs,
    avgRunMs:              b.avgRunMs,
    p50: b.p50, p90: b.p90, p99: b.p99,
    errors: b.errorCount,
  }));
  const totalRuns   = series.reduce((acc, b) => acc + (b.runs || 0), 0);
  const totalTasks  = series.reduce((acc, b) => acc + (b.tasks || 0), 0);
  const totalErrors = series.reduce((acc, b) => acc + (b.errors || 0), 0);

  // Headline cumulatives — backend computes these as cumulative-final, so
  // they're identical to summing the per-bucket counts but free of any
  // bucket-edge double-counting risk.
  const totalDispatched = data.totalsDispatched ?? totalTasks;
  const totalStarted    = data.totalsStarted    ?? 0;
  const totalCompleted  = data.totalsCompleted  ?? 0;

  // Decide units once for the latency-drift Y axis: anything ≥ 10s reads
  // better in seconds; otherwise stick with ms.
  const maxLatency = series.reduce((m, b) => Math.max(m, b.p99 || 0, b.p90 || 0, b.p50 || 0), 0);
  const latencyAxisFmt = (v) => fmtMsAuto(v, maxLatency);

  // Backlog headline stats — computed across the visible window.
  const peakPending = series.reduce((m, b) => Math.max(m, b.tasksPending || 0), 0);
  const peakActive  = series.reduce((m, b) => Math.max(m, b.tasksActive  || 0), 0);
  const queueWaits  = series.map((b) => b.avgQueueWaitMs).filter((v) => v != null && v >= 0);
  const runMses     = series.map((b) => b.avgRunMs).filter((v) => v != null && v >= 0);
  const avgQueueWaitMs = queueWaits.length
    ? queueWaits.reduce((a, x) => a + x, 0) / queueWaits.length : null;
  const avgRunMs = runMses.length
    ? runMses.reduce((a, x) => a + x, 0) / runMses.length : null;
  // Drain rate = average completed/sec in buckets where dispatched/sec=0.
  // Tells the operator how fast the worker pool clears a queue once demand
  // stops, which is a different number from "throughput under load".
  const drainBuckets = series.filter(
    (b) => (b.tasksDispatchedPerSec || 0) === 0 && (b.tasksCompletedPerSec || 0) > 0
  );
  const drainRate = drainBuckets.length
    ? drainBuckets.reduce((a, b) => a + b.tasksCompletedPerSec, 0) / drainBuckets.length
    : null;

  // Capacity ceiling — fetched once from the worker-config endpoint. We
  // overlay this as a reference line on the saturation chart so operators
  // can immediately see "active is hitting the wall". Endpoint is admin-
  // only; if the user can't read it (e.g. demo mode) we just hide the line
  // rather than failing the whole panel.
  const [capacityCeiling, setCapacityCeiling] = useState(null);
  useEffect(() => {
    let alive = true;
    api.getTemporalWorkerConfig()
      .then((res) => {
        if (!alive || !res?.ok) return;
        const v = res.data?.worker?.maxConcurrentActivityExecutions;
        if (typeof v === 'number') setCapacityCeiling(v);
      })
      .catch(() => { /* hide line if forbidden / not reachable */ });
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* Bucket explanation: the user complained "what are buckets?" — make
            it self-explanatory in the card subtitle and tooltip. */}
        <StatCard label="Buckets" value={fmtN(data.bucketCount)}
          accent={ACCENT.slate} icon={Layers}
          subtitle={`each = ${bucketSize} (${bucketSeconds}s) — chart slices`}
          tooltip={SOAK_GLOSSARY.buckets} />
        <StatCard label="Total workflow runs" value={fmtN(totalRuns)}
          accent={ACCENT.indigo} icon={Activity}
          subtitle={`avg ${fmtPerSec(totalRuns / Math.max(1, data.bucketCount * bucketSeconds))}`}
          tooltip={SOAK_GLOSSARY.totalRuns} />
        <StatCard label="Total task runs" value={fmtN(totalTasks)}
          accent={ACCENT.sky} icon={Cpu}
          subtitle={`avg ${fmtPerSec(totalTasks / Math.max(1, data.bucketCount * bucketSeconds))}`}
          tooltip={SOAK_GLOSSARY.totalTasks} />
        <StatCard label="Total errors" value={fmtN(totalErrors)}
          accent={ACCENT.rose} icon={XCircle}
          subtitle={totalRuns ? `${((totalErrors / totalRuns) * 100).toFixed(2)}% rate` : '—'}
          tooltip={SOAK_GLOSSARY.totalErrors} />
      </div>

      {/* Pipeline headline strip — three cards mirror the three lines on the
          throughput chart so operators can read the demand→throughput→drain
          story without staring at a legend. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Dispatched" value={fmtN(totalDispatched)}
          accent={ACCENT.indigo} icon={TrendingUp}
          subtitle="tasks created by DAG executor"
          tooltip={SOAK_GLOSSARY.dispatched} />
        <StatCard label="Started" value={fmtN(totalStarted)}
          accent={ACCENT.sky} icon={Cpu}
          subtitle="tasks picked up by a worker"
          tooltip={SOAK_GLOSSARY.started} />
        <StatCard label="Completed" value={fmtN(totalCompleted)}
          accent={ACCENT.emerald} icon={CheckCircle}
          subtitle="tasks finished (success or fail)"
          tooltip={SOAK_GLOSSARY.completed} />
        <StatCard label="In-flight residual"
          value={fmtN(Math.max(0, totalDispatched - totalCompleted))}
          accent={ACCENT.amber} icon={Loader}
          subtitle="dispatched − completed at window end"
          tooltip={
            'Tasks created during this window that have not yet completed. ' +
            'Steady residual ≈ in-flight depth at window end. Growing residual ' +
            'across consecutive refreshes = unresolved backlog.'
          } />
      </div>

      <Panel title="Throughput pipeline"
        subtitle="Dispatched / Started / Completed per second — gaps between lines reveal queue growth (dispatched−started) and in-flight depth (started−completed)">
        <div style={{ height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: CHART.axis }}
                interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: CHART.axis }}
                tickFormatter={(v) => `${v.toFixed(v < 1 ? 2 : v < 10 ? 1 : 0)}/s`} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(value, name) => [fmtPerSec(value), name]} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Line type="monotone" dataKey="tasksDispatchedPerSec" name="Dispatched/sec"
                stroke={CHART.primary} strokeWidth={2.2} dot={false} />
              <Line type="monotone" dataKey="tasksStartedPerSec" name="Started/sec"
                stroke={CHART.secondary} strokeWidth={1.8} dot={false} />
              <Line type="monotone" dataKey="tasksCompletedPerSec" name="Completed/sec"
                stroke={CHART.pct.p50} strokeWidth={1.8} dot={false} />
              <Line type="monotone" dataKey="workflowsPerSec" name="Workflows/sec"
                stroke={S[400]} strokeWidth={1.2} strokeDasharray="3 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel title="Backlog & worker saturation"
        subtitle={
          'Pending (queued, awaiting pickup) stacked over Active (running on a worker)' +
          (capacityCeiling != null
            ? ` — red dashed line is the per-worker capacity ceiling (max-concurrent-activity-executions = ${fmtN(capacityCeiling)})`
            : '')
        }>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 16, padding: 16 }}>
          <div style={{ height: 260, minWidth: 0 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <defs>
                  <linearGradient id="bkActive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.secondary} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={CHART.secondary} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="bkPending" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT.amber.icon} stopOpacity={0.50} />
                    <stop offset="100%" stopColor={ACCENT.amber.icon} stopOpacity={0.06} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: CHART.axis }}
                  interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: CHART.axis }}
                  tickFormatter={(v) => fmtN(v)} />
                <Tooltip contentStyle={tooltipStyle}
                  formatter={(value, name) => [fmtN(value), name]} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Area type="monotone" stackId="bk" dataKey="tasksActive" name="Active"
                  stroke={CHART.secondary} fill="url(#bkActive)" strokeWidth={1.6} />
                <Area type="monotone" stackId="bk" dataKey="tasksPending" name="Pending"
                  stroke={ACCENT.amber.icon} fill="url(#bkPending)" strokeWidth={1.6} />
                {capacityCeiling != null && (
                  <ReferenceLine y={capacityCeiling}
                    stroke={ACCENT.rose.icon} strokeDasharray="4 4" strokeWidth={1.4}
                    label={{
                      value: `Capacity ${fmtN(capacityCeiling)}`,
                      position: 'right', fontSize: 10, fill: ACCENT.rose.icon,
                    }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Right-rail mini-stats — these are the numbers an operator wants to
              quote in a war-room incident bridge ("queue-wait peaked at 3.2s,
              drain rate is 410/s"). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MiniStat label="Peak pending"   value={fmtN(peakPending)}
              tone={ACCENT.amber}   tooltip={SOAK_GLOSSARY.pending} />
            <MiniStat label="Peak active"    value={fmtN(peakActive)}
              tone={ACCENT.sky}     tooltip={SOAK_GLOSSARY.active} />
            <MiniStat label="Avg queue wait" value={fmtMs(avgQueueWaitMs)}
              tone={ACCENT.violet}  tooltip={SOAK_GLOSSARY.queueWait} />
            <MiniStat label="Avg run time"   value={fmtMs(avgRunMs)}
              tone={ACCENT.indigo}  tooltip={SOAK_GLOSSARY.runMs} />
            <MiniStat label="Drain rate"
              value={drainRate != null ? fmtPerSec(drainRate) : '—'}
              tone={ACCENT.emerald} tooltip={SOAK_GLOSSARY.drainRate}
              subtitle={drainBuckets.length
                ? `${drainBuckets.length} idle bucket${drainBuckets.length === 1 ? '' : 's'}`
                : 'no idle buckets'} />
          </div>
        </div>
      </Panel>

      <Panel title="Latency drift"
        subtitle={`P50 / P90 / P99 per bucket — Y-axis auto-formatted in ${maxLatency >= 10000 ? 'seconds' : 'milliseconds'} for readability`}>
        <div style={{ height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: CHART.axis }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: CHART.axis }} tickFormatter={latencyAxisFmt} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(value, name) => [fmtMs(value), name]} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Line type="monotone" dataKey="p50" name="P50"
                stroke={CHART.pct.p50} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="p90" name="P90"
                stroke={CHART.pct.p90} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="p99" name="P99"
                stroke={CHART.pct.p99} strokeWidth={2.4} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
}

/**
 * Compact metric tile used inside the right-rail of the Backlog panel.
 * Same visual language as StatCard but denser (no icon, smaller type) so
 * 5 of them stack neatly beside a chart without dominating it.
 */
function MiniStat({ label, value, tone, tooltip, subtitle }) {
  const a = tone || ACCENT.slate;
  return (
    <div title={tooltip || undefined}
      style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8,
        padding: '10px 12px', cursor: tooltip ? 'help' : 'default',
        position: 'relative', overflow: 'hidden',
      }}>
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: a.icon }} />
      <div style={{
        fontSize: 9, fontWeight: 700, color: a.text, textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 4, paddingLeft: 4,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        {label}
        {tooltip && <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 11, height: 11, borderRadius: '50%',
          border: `1px solid ${a.border}`, background: a.bg, color: a.text,
          fontSize: 7, fontWeight: 800,
        }}>?</span>}
      </div>
      <div style={{
        fontSize: 17, fontWeight: 800, color: S[900], lineHeight: 1.1,
        letterSpacing: '-0.01em', fontFeatureSettings: "'tnum'", paddingLeft: 4,
      }}>{value}</div>
      {subtitle && (
        <div style={{ fontSize: 10, color: S[500], marginTop: 3, paddingLeft: 4 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   AI ANALYST VIEW — LLM-generated performance analysis (Phase-3)

   The server assembles a briefing from the same data the dashboards render
   and asks Claude for a strict-JSON analysis with four sections:

     • summary          — 2-3 sentence executive headline
     • posture          — healthy / degraded / critical
     • findings[]       — severity-ranked observations citing exact metric values
     • recommendations[]— prescriptive fixes with paste-ready property snippets
     • comparisons[]    — observed vs healthy-target benchmarks

   We render all four sections deterministically — even if the briefing was
   sparse and Claude returned mostly empty arrays — so the layout is stable
   regardless of how much there is to say.
   ══════════════════════════════════════════════════════════════════════════════ */

const AI_SEVERITY_TONE = {
  critical: ACCENT.rose,
  high:     ACCENT.amber,
  medium:   ACCENT.sky,
  low:      ACCENT.slate,
  unknown:  ACCENT.slate,
};

const AI_PRIORITY_TONE = {
  P0: ACCENT.rose,
  P1: ACCENT.amber,
  P2: ACCENT.indigo,
};

const AI_POSTURE = {
  healthy:  { label: 'Healthy',  tone: ACCENT.emerald, icon: CheckCircle },
  degraded: { label: 'Degraded', tone: ACCENT.amber,   icon: AlertTriangle },
  critical: { label: 'Critical', tone: ACCENT.rose,    icon: XCircle },
  unknown:  { label: 'Unknown',  tone: ACCENT.slate,   icon: Brain },
};

function AIAnalystView({ data, loading, error, workflowId, workflowName,
                        windowLabel, onAnalyze, onRefresh }) {
  // Empty-state CTA — no analysis run yet for this filter combination.
  if (!data && !loading && !error) {
    return (
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        padding: 40, textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 16, background: BRAND.gradientSubtle,
          border: `1px solid ${BRAND.soft}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 18,
        }}>
          <Brain size={28} color={BRAND.primary} />
        </div>
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: S[900],
          letterSpacing: '-0.01em' }}>
          AI-powered performance review
        </h3>
        <p style={{ margin: '0 auto 18px', maxWidth: 520, fontSize: 12, color: S[500],
          lineHeight: 1.55 }}>
          Sends the same numbers the dashboards render — performance, time-series,
          hot-spots, protection — to Claude and asks for severity-ranked findings
          and prescriptive tuning recommendations citing exact Spring properties.
          Strictly on-demand: no LLM calls happen until you click below.
        </p>
        <div style={{ fontSize: 11, color: S[500], marginBottom: 20 }}>
          Scope:&nbsp;
          <span style={{ color: S[700], fontWeight: 600 }}>
            {workflowName ? workflowName : workflowId ? 'workflow' : 'tenant-wide'}
          </span>
          &nbsp;·&nbsp;Window:&nbsp;
          <span style={{ color: S[700], fontWeight: 600 }}>{windowLabel || '—'}</span>
        </div>
        <button onClick={onAnalyze}
          disabled={loading}
          style={{
            ...btnBase, padding: '10px 20px', fontSize: 13, fontWeight: 700,
            background: BRAND.gradient, color: '#fff', border: 'none',
            boxShadow: '0 1px 3px rgba(226,74,58,0.30)',
          }}>
          <Sparkles size={14} />
          Analyze this window
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        padding: 40, textAlign: 'center',
      }}>
        <Loader size={26} color={BRAND.primary} style={{ animation: 'wipSpin 1s linear infinite' }} />
        <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: S[700] }}>
          Generating analysis…
        </div>
        <div style={{ fontSize: 11, color: S[500], marginTop: 4 }}>
          Briefing Claude with this window's metrics — typically 4–8 seconds.
        </div>
      </div>
    );
  }

  if (error) {
    return <BodyError message={error} />;
  }

  if (!data) return null;

  // Disabled = the server has no LLM provider configured. Render the
  // explanation rather than pretending the call was made.
  if (data.enabled === false) {
    return (
      <div style={{
        background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
        borderRadius: 12, padding: 20, color: ACCENT.amber.text, fontSize: 12,
        lineHeight: 1.5,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
          fontSize: 13, fontWeight: 700 }}>
          <AlertTriangle size={14} />
          AI Analyst is disabled
        </div>
        {data.summary}
      </div>
    );
  }

  // Inline error from the server (e.g. LLM call failed at the provider).
  if (data.error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <BodyError message={data.error} />
        <div>
          <button onClick={() => onRefresh()} style={{
            ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
            background: BRAND.primary, color: '#fff', border: 'none',
          }}>
            <RefreshCw size={13} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const posture = AI_POSTURE[data.posture] || AI_POSTURE.unknown;
  const PostureIcon = posture.icon;
  const findings        = Array.isArray(data.findings)        ? data.findings        : [];
  const recommendations = Array.isArray(data.recommendations) ? data.recommendations : [];
  const comparisons     = Array.isArray(data.comparisons)     ? data.comparisons     : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ─── Headline card ─────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${S[100]}`,
          background: BRAND.gradientSubtle,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: posture.tone.bg,
              border: `1px solid ${posture.tone.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PostureIcon size={18} color={posture.tone.icon} strokeWidth={2.2} />
            </div>
            <div>
              <div style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: S[500], marginBottom: 2,
              }}>Posture</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: S[900],
                letterSpacing: '-0.02em' }}>
                {posture.label}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {data.cached && (
              <span title={`Cached ${data.cacheAgeSeconds || 0}s ago — click Re-analyze for a fresh LLM run.`}
                style={{
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                  background: ACCENT.slate.bg, color: ACCENT.slate.text,
                  border: `1px solid ${ACCENT.slate.border}`,
                  cursor: 'help',
                }}>cached</span>
            )}
            <button onClick={() => onRefresh()} title="Bypass the cache and ask the model again"
              style={{
                ...btnBase, padding: '7px 12px', fontSize: 11, fontWeight: 600,
                background: '#fff', color: S[700], border: `1px solid ${S[300]}`,
              }}>
              <RefreshCw size={12} /> Re-analyze
            </button>
          </div>
        </div>
        <div style={{ padding: '16px 20px', fontSize: 13, color: S[800], lineHeight: 1.55 }}>
          {data.summary || <span style={{ color: S[400] }}>No summary available.</span>}
        </div>
        {/* Footnote — what the analyst saw and what it cost to ask. */}
        <div style={{
          padding: '10px 20px', borderTop: `1px solid ${S[100]}`, background: S[25],
          display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 10, color: S[500],
        }}>
          <span><strong style={{ color: S[700] }}>{data.model || '—'}</strong> · {data.providerType || '—'}</span>
          <span>{data.inputTokens != null ? `${fmtN(data.inputTokens)} in` : ''}{' '}
            {data.outputTokens != null ? `· ${fmtN(data.outputTokens)} out` : ''}</span>
          <span>{data.latencyMs != null ? `${(data.latencyMs / 1000).toFixed(1)}s` : ''}</span>
          <span style={{ marginLeft: 'auto' }}>generated&nbsp;
            <span style={{ color: S[700] }}>{data.generatedAt ? shortTime(data.generatedAt) : '—'}</span>
          </span>
        </div>
      </div>

      {/* ─── Findings panel ────────────────────────────────────────────── */}
      <Panel title={`Findings (${findings.length})`}
        subtitle="Severity-ranked observations grounded in the metrics above. Each finding cites the exact numbers it's based on.">
        {findings.length === 0 ? (
          <BodyEmpty msg="No notable issues detected in this window." />
        ) : (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {findings.map((f, i) => <AIFindingCard key={i} finding={f} />)}
          </div>
        )}
      </Panel>

      {/* ─── Recommendations panel ─────────────────────────────────────── */}
      <Panel title={`Recommendations (${recommendations.length})`}
        subtitle="Prescriptive tuning steps. Snippets are paste-ready — drop them into application.yml or the corresponding infrastructure surface.">
        {recommendations.length === 0 ? (
          <BodyEmpty msg="No actionable recommendations for this window." />
        ) : (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recommendations.map((r, i) => <AIRecommendationCard key={i} rec={r} />)}
          </div>
        )}
      </Panel>

      {/* ─── Comparisons panel ─────────────────────────────────────────── */}
      <Panel title="Benchmark comparisons"
        subtitle="Observed values vs healthy production targets. Verdict 'over' = breaching target; 'near' = within 20% of target; 'under' = well inside.">
        {comparisons.length === 0 ? (
          <BodyEmpty msg="No comparable benchmarks for this window." />
        ) : (
          <AIComparisonTable comparisons={comparisons} />
        )}
      </Panel>

      {/* ─── Parse error fallback (rare — model returned non-JSON) ────── */}
      {data.parseError && (
        <Panel title="Raw model output"
          subtitle={`Couldn't parse the model's response as JSON (${data.parseError}). Showing the raw text below — please retry; recurrence may indicate a transient provider issue.`}>
          <pre style={{
            margin: 0, padding: 16, fontSize: 11, color: S[700], background: S[50],
            border: `1px solid ${S[100]}`, borderRadius: 0, overflow: 'auto',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {data.raw || ''}
          </pre>
        </Panel>
      )}
    </div>
  );
}

function AIFindingCard({ finding }) {
  const tone = AI_SEVERITY_TONE[(finding.severity || '').toLowerCase()] || AI_SEVERITY_TONE.unknown;
  const evidence = Array.isArray(finding.evidence) ? finding.evidence : [];
  return (
    <div style={{
      border: `1px solid ${S[200]}`, borderRadius: 10, padding: '12px 14px',
      background: '#fff', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: tone.icon }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 8 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
          background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>{(finding.severity || 'unknown')}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: S[900], letterSpacing: '-0.01em' }}>
          {finding.title || 'Untitled finding'}
        </span>
      </div>
      {finding.detail && (
        <div style={{ fontSize: 12, color: S[700], lineHeight: 1.5, paddingLeft: 8 }}>
          {finding.detail}
        </div>
      )}
      {evidence.length > 0 && (
        <div style={{
          marginTop: 8, paddingLeft: 8,
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {evidence.map((e, i) => (
            <code key={i} style={{
              fontSize: 10, padding: '2px 7px', borderRadius: 4,
              background: S[50], color: S[700], border: `1px solid ${S[150]}`,
              fontFamily: "'IBM Plex Mono', monospace",
            }}>{String(e)}</code>
          ))}
        </div>
      )}
    </div>
  );
}

function AIRecommendationCard({ rec }) {
  const tone = AI_PRIORITY_TONE[(rec.priority || '').toUpperCase()] || ACCENT.indigo;
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    if (!rec.snippet) return;
    try {
      await navigator.clipboard.writeText(rec.snippet);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };
  return (
    <div style={{
      border: `1px solid ${S[200]}`, borderRadius: 10, padding: '12px 14px',
      background: '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
          background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
          letterSpacing: '0.06em',
        }}>{(rec.priority || 'P2').toUpperCase()}</span>
        {rec.changeType && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            background: ACCENT.slate.bg, color: ACCENT.slate.text,
            border: `1px solid ${ACCENT.slate.border}`,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{rec.changeType}</span>
        )}
        <span style={{ fontSize: 13, fontWeight: 700, color: S[900], letterSpacing: '-0.01em' }}>
          <Wrench size={11} style={{ verticalAlign: 'middle', marginRight: 4, color: tone.icon }} />
          {rec.title || 'Untitled recommendation'}
        </span>
      </div>
      {rec.rationale && (
        <div style={{ fontSize: 12, color: S[700], lineHeight: 1.5, marginBottom: 8 }}>
          {rec.rationale}
        </div>
      )}
      {rec.snippet && (
        <div style={{ position: 'relative' }}>
          <pre style={{
            margin: 0, padding: '10px 12px', paddingRight: 70,
            fontSize: 11, color: S[800], background: S[50],
            border: `1px solid ${S[150]}`, borderRadius: 6,
            fontFamily: "'IBM Plex Mono', monospace",
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>{rec.snippet}</pre>
          <button onClick={onCopy} title="Copy snippet"
            style={{
              ...btnBase, position: 'absolute', top: 6, right: 6,
              padding: '4px 8px', fontSize: 10, fontWeight: 600,
              background: '#fff', color: S[600], border: `1px solid ${S[200]}`,
            }}>
            <CopyIcon size={10} /> {copied ? 'copied' : 'copy'}
          </button>
        </div>
      )}
      {rec.expectedImpact && (
        <div style={{
          marginTop: 8, fontSize: 11, color: S[600], lineHeight: 1.4,
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <Target size={11} style={{ marginTop: 2, color: ACCENT.emerald.icon, flexShrink: 0 }} />
          <span><strong style={{ color: S[700] }}>Expected impact:</strong> {rec.expectedImpact}</span>
        </div>
      )}
    </div>
  );
}

function AIComparisonTable({ comparisons }) {
  const verdictTone = (v) => v === 'over' ? ACCENT.rose
                          : v === 'near' ? ACCENT.amber
                          : v === 'under' ? ACCENT.emerald
                          : ACCENT.slate;
  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 0.8fr 2fr' }}>
        <TCell hdr>Metric</TCell>
        <TCell hdr style={{ textAlign: 'right' }}>Observed</TCell>
        <TCell hdr style={{ textAlign: 'right' }}>Healthy target</TCell>
        <TCell hdr>Verdict</TCell>
        <TCell hdr>Note</TCell>
        {comparisons.map((c, i) => {
          const tone = verdictTone(c.verdict);
          return (
            <React.Fragment key={i}>
              <TCell><code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: S[800] }}>{c.metric || '—'}</code></TCell>
              <TCell style={{ textAlign: 'right', fontFeatureSettings: "'tnum'" }}>{fmtComparisonValue(c.metric, c.observed)}</TCell>
              <TCell style={{ textAlign: 'right', fontFeatureSettings: "'tnum'" }}>{fmtComparisonValue(c.metric, c.healthyTarget)}</TCell>
              <TCell>
                <span style={{
                  fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4,
                  background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{c.verdict || 'unknown'}</span>
              </TCell>
              <TCell style={{ color: S[600] }}>{c.note || ''}</TCell>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Format a comparison value based on the metric name. The model returns
 * raw numbers (e.g. p99Ms=2400, errorRate=0.012); we render them with
 * appropriate units so the verdict reads naturally.
 */
function fmtComparisonValue(metric, value) {
  if (value == null || isNaN(value)) return '—';
  const m = (metric || '').toLowerCase();
  if (m.includes('rate') || m.includes('ratio') || m.includes('percent')) {
    return `${(value * 100).toFixed(2)}%`;
  }
  if (m.endsWith('ms') || m.includes('latency') || m.includes('wait') || m.includes('duration')) {
    return fmtMs(value);
  }
  if (m.includes('persec') || m.includes('throughput')) {
    return fmtPerSec(value);
  }
  return fmtN(value);
}

/* ══════════════════════════════════════════════════════════════════════════════
   GOVERNANCE VIEW — stage decision rates + DPG findings density
   ══════════════════════════════════════════════════════════════════════════════ */

function GovernanceView({ data }) {
  const stages = data.stages || [];
  const STAGE_LABEL = {
    PRE_GATE: 'Pre-Gate', DPG_SCAN: 'DPG Scan', POLICY_EVAL: 'Policy Eval',
    DECISION: 'Decision', EXECUTION: 'Execution',
    POST_AUDIT: 'Audit', POST_RECORD: 'Post-Record',
  };
  const series = stages.map((s) => ({
    label: STAGE_LABEL[s.stage] || s.stage,
    allowed: s.allowed, warned: s.warned, blocked: s.blocked,
    avgEvalMs: s.avgEvalMs,
  }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Runs" value={fmtN(data.runCount)} accent={ACCENT.slate} icon={Activity} />
        <StatCard label="Clean runs" value={fmtN(data.runsClean)} accent={ACCENT.emerald} icon={CheckCircle}
          subtitle={data.runCount ? `${((data.runsClean / data.runCount) * 100).toFixed(1)}%` : '—'} />
        <StatCard label="Warned runs" value={fmtN(data.runsWarned)} accent={ACCENT.amber} icon={AlertTriangle}
          subtitle={data.runCount ? `${((data.runsWarned / data.runCount) * 100).toFixed(1)}%` : '—'} />
        <StatCard label="Blocked runs" value={fmtN(data.runsBlocked)} accent={ACCENT.rose} icon={XCircle}
          subtitle={data.runCount ? `${((data.runsBlocked / data.runCount) * 100).toFixed(1)}%` : '—'} />
      </div>

      <Panel title="Stage decision distribution" subtitle="Stacked decisions per enforcement stage">
        <div style={{ height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: CHART.axis }} />
              <YAxis tick={{ fontSize: 10, fill: CHART.axis }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="allowed" name="Allow" stackId="a" fill={CHART.gov.ok}    radius={[0, 0, 0, 0]} />
              <Bar dataKey="warned"  name="Warn"  stackId="a" fill={CHART.gov.warn}  radius={[0, 0, 0, 0]} />
              <Bar dataKey="blocked" name="Block" stackId="a" fill={CHART.gov.block} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel title="Stage rollup" subtitle="Per-stage tally with average evaluation time">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,1fr) 80px 80px 80px 80px 100px',
          fontSize: 12 }}>
          <TCell hdr>Stage</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Fired</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Allow</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Warn</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Block</TCell>
          <TCell hdr style={{ textAlign: 'right' }}>Avg eval</TCell>
          {stages.map((s) => (
            <React.Fragment key={s.stage}>
              <TCell><strong style={{ color: S[800] }}>{STAGE_LABEL[s.stage] || s.stage}</strong></TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: S[700] }}>
                {fmtN(s.fired)}
              </TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: s.allowed > 0 ? ACCENT.emerald.text : S[400] }}>
                {fmtN(s.allowed)}
              </TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: s.warned > 0 ? ACCENT.amber.text : S[400] }}>
                {fmtN(s.warned)}
              </TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                color: s.blocked > 0 ? ACCENT.rose.text : S[400] }}>
                {fmtN(s.blocked)}
              </TCell>
              <TCell style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                fontFamily: "'IBM Plex Mono', monospace", color: S[700] }}>
                {fmtMs(s.avgEvalMs)}
              </TCell>
            </React.Fragment>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function StatCard({ label, value, accent, icon: Icon, subtitle, tooltip }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="wip-stat-card" title={tooltip || undefined} style={{
      flex: 1, minWidth: 200, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: tooltip ? 'help' : 'default',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            {label}
            {tooltip && <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 12, height: 12, borderRadius: '50%',
              border: `1px solid ${a.border}`, background: a.bg, color: a.text,
              fontSize: 8, fontWeight: 800,
            }}>?</span>}
          </div>
          <div style={{
            fontSize: 26, fontWeight: 800, color: S[900], lineHeight: 1,
            letterSpacing: '-0.02em', fontFeatureSettings: "'tnum'",
          }}>{value}</div>
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 6, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg,
          border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <div className="wip-card" style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${S[100]}`, background: S[25] }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: S[900], letterSpacing: '-0.005em' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function TCell({ children, hdr, style, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: hdr ? '8px 14px' : '10px 14px',
      borderBottom: `1px solid ${S[100]}`,
      background: hdr ? S[50] : 'transparent',
      fontSize: hdr ? 9 : 12,
      fontWeight: hdr ? 700 : 400,
      color: hdr ? S[500] : S[700],
      textTransform: hdr ? 'uppercase' : 'none',
      letterSpacing: hdr ? '0.06em' : 0,
      ...(style || {}),
    }}>
      {children}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || 'unknown').toLowerCase();
  const a = s === 'completed' ? ACCENT.emerald
          : s === 'failed' ? ACCENT.rose
          : s === 'running' || s === 'pending' ? ACCENT.sky
          : ACCENT.slate;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: a.bg, border: `1px solid ${a.border}`, color: a.text,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      {s}
    </span>
  );
}

function BodyLoading() {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: S[500] }}>
      <Loader size={26} style={{ animation: 'wipSpin 1s linear infinite' }} />
      <div style={{ fontSize: 13, marginTop: 12 }}>Crunching analytics…</div>
    </div>
  );
}

function BodyError({ message }) {
  return (
    <div style={{
      padding: '14px 16px', background: ACCENT.rose.bg,
      border: `1px solid ${ACCENT.rose.border}`, color: ACCENT.rose.text,
      borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <AlertTriangle size={16} color={ACCENT.rose.icon} />
      {message}
    </div>
  );
}

function BodyEmpty({ msg }) {
  return (
    <div style={{
      padding: 60, textAlign: 'center', color: S[500],
      background: '#fff', border: `1px dashed ${S[200]}`, borderRadius: 12,
    }}>
      <BarChart3 size={32} color={S[300]} />
      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 12, color: S[700] }}>
        Nothing to chart yet
      </div>
      <div style={{ fontSize: 12, marginTop: 6, maxWidth: 460, margin: '6px auto 0' }}>
        {msg || 'Try widening the time window or running the workflow first.'}
      </div>
    </div>
  );
}

/* ─── Helpers ───────────────────────────────────────────────────────────── */

const tooltipStyle = {
  background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 11, padding: '8px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};

function fmtMs(v) {
  if (v == null) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  if (v < 60000) return `${(v / 1000).toFixed(2)} s`;
  return `${(v / 60000).toFixed(1)} m`;
}
/** Auto-scale a millisecond axis tick. Works on bare numbers (Recharts passes
 *  the raw axis value into the formatter). */
function fmtMsAuto(v, maxMs) {
  if (v == null || isNaN(v)) return '';
  if (maxMs != null && maxMs >= 10_000) {
    return `${(v / 1000).toFixed(v < 1000 ? 2 : 1)} s`;
  }
  return `${Math.round(v)} ms`;
}
function fmtN(v) {
  if (v == null) return '—';
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(1)}k`;
  return `${(v / 1_000_000).toFixed(2)}M`;
}
/** Compact rate formatter, e.g. 0.42/s, 12.3/s, 1.4k/s, 2.1M/s */
function fmtPerSec(v) {
  if (v == null || !isFinite(v)) return '—';
  if (v < 1) return `${v.toFixed(2)}/s`;
  if (v < 100) return `${v.toFixed(1)}/s`;
  if (v < 1000) return `${Math.round(v)}/s`;
  if (v < 1_000_000) return `${(v / 1000).toFixed(1)}k/s`;
  return `${(v / 1_000_000).toFixed(2)}M/s`;
}
/** "12s ago", "3m ago", "1h ago" — for last-refreshed timestamps. */
function fmtTimeAgo(date) {
  if (!date) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
function histLabel(b) {
  if (b.bucketHighMs == null) return `${fmtMs(b.bucketLowMs)}+`;
  return `<${fmtMs(b.bucketHighMs)}`;
}
function histFill(i, total) {
  // Cool→warm enterprise ramp — bucket index encodes "fast→slow" intuitively.
  return CHART.histo[Math.min(CHART.histo.length - 1, Math.round((i / Math.max(1, total - 1)) * (CHART.histo.length - 1)))];
}
function shortTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = d.getMonth() + 1 + '/' + d.getDate();
  const time = d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
  return `${day} ${time}`;
}

/* ══════════════════════════════════════════════════════════════════════════════
   BENCHMARKS VIEW — Phase-12B
   Stress / endurance / soak test history. Three modes:
     • LIST    — table of recent runs with name / status / duration / totals
     • DETAIL  — per-stage table + snapshot timeline + Export PDF/XLSX
     • COMPARE — side-by-side A/B with per-stage Δ p50/p95/p99
   Mode switching is internal to BenchmarksView (no URL state) — keeps the
   tab self-contained and reloadable.
   ══════════════════════════════════════════════════════════════════════════════ */
function BenchmarksView({ fromInstant, windowLabel }) {
  // We always load the full benchmark history once and filter client-side
  // by startedAt — the dataset is small (rare events, not per-run rows) so
  // this is the simplest correct path; no extra API parameter needed.
  const [allRuns, setAllRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState('list');           // 'list' | 'detail' | 'compare'
  const [selectedId, setSelectedId] = useState(null); // detail view target
  const [detail, setDetail] = useState(null);
  // Server-measured workflow performance for the detail view's run
  // window (perf-debug 2026-05-02 #131). Loaded in parallel with detail.
  // null while loading or if the run has no startedAt.
  const [serverPerf, setServerPerf] = useState(null);
  // Tail buffer (seconds added after run.completedAt) for the
  // server-metrics window. 60s default keeps latency aggregation honest
  // (workflows that finished just after the harness stopped still count
  // toward percentiles). Toggling to 0 ("firing window only") makes the
  // sustained-rate calc match k6's reported request rate exactly — at
  // the cost of clipping late-completing latency samples. Persisted in
  // local component state only; defaults to 60 every detail open.
  const [tailSec, setTailSec] = useState(60);
  // Tracks the in-flight server-metrics fetch sequence so a stale
  // response from a previous toggle click can't overwrite a newer one.
  const serverPerfFetchSeq = useRef(0);
  // Up to 6 runs side-by-side. compareIds[0] is the BASELINE; deltas are
  // computed against it for every other run. Empty slots stay null.
  const [compareIds, setCompareIds] = useState([null, null, null]);
  const [compareData, setCompareData] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Apply the parent's window filter — keep any run whose startedAt falls
  // inside the window. Runs without a timestamp slip through so an in-progress
  // record still shows up while it's running.
  const runs = useMemo(() => {
    if (!fromInstant) return allRuns;
    const cutoff = new Date(fromInstant).getTime();
    return allRuns.filter((r) => {
      if (!r.startedAt) return true;
      const t = new Date(r.startedAt).getTime();
      return Number.isFinite(t) ? t >= cutoff : true;
    });
  }, [allRuns, fromInstant]);

  // ── Load run list ──
  const loadRuns = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.listBenchmarks();
      setAllRuns(res.ok ? (Array.isArray(res.data) ? res.data : []) : []);
      if (!res.ok) setErr(res.error || 'Failed to load benchmark runs');
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  // Server-metrics fetcher pulled out so the tail-buffer toggle can
  // re-trigger it without re-fetching the benchmark entity itself. Uses
  // serverPerfFetchSeq to ignore stale responses (e.g., user double-
  // clicks the toggle and the first response arrives after the second).
  const refetchServerMetrics = useCallback(async (id, tail) => {
    if (!id) return;
    const seq = ++serverPerfFetchSeq.current;
    try {
      const sres = await api.getBenchmarkServerMetrics(id, tail);
      if (seq !== serverPerfFetchSeq.current) return; // stale, drop
      if (sres.ok && sres.data && Object.keys(sres.data || {}).length > 0) {
        setServerPerf(sres.data);
      } else {
        setServerPerf(null);
      }
    } catch (e) {
      if (seq !== serverPerfFetchSeq.current) return;
      // Soft error — the panel just won't render. Logged for diagnostics.
      console.warn('benchmark server-metrics fetch failed:', e.message);
      setServerPerf(null);
    }
  }, []);

  // ── Detail loader ──
  // Loads BOTH the benchmark entity (with the harness-POSTed
  // aggregateMetrics) AND a fresh server-measured performance snapshot
  // for the run's exact wall-clock window. Perf-debug 2026-05-02 (#131):
  // earlier UI showed only the client-side latencies which got distorted
  // by harness-internal saturation; surfacing the server window lets the
  // operator compare the two and see when the script is self-throttling.
  const openDetail = useCallback(async (id) => {
    setMode('detail'); setSelectedId(id); setDetail(null); setServerPerf(null);
    // Reset tail buffer to default whenever we open a new run — operators
    // expect the better-aggregated latency view first; toggle resets to 0
    // would otherwise persist across run switches and surprise people.
    setTailSec(60);
    try {
      const res = await api.getBenchmark(id);
      if (res.ok) setDetail(res.data);
      else setErr(res.error || 'Failed to load benchmark');
    } catch (e) { setErr(e.message); }
    // Fire-and-forget the server-metrics fetch in parallel — UI can
    // render the client side immediately and slot the server panel in
    // when the second call returns. Failures are non-fatal: show the
    // run with no server panel rather than blocking the page.
    refetchServerMetrics(id, 60);
  }, [refetchServerMetrics]);

  // Toggle handler: update the tail-buffer state AND immediately fire
  // a refetch with the new value. We do this here rather than in a
  // useEffect because:
  //   (a) the project's ESLint config doesn't load react-hooks/exhaustive-deps,
  //       so we can't suppress the dep-array warning cleanly, and
  //   (b) this is also more direct — the refetch is a deterministic
  //       consequence of the click, not a side effect of state.
  const handleTailSecChange = useCallback((next) => {
    setTailSec(next);
    if (mode === 'detail' && selectedId) {
      refetchServerMetrics(selectedId, next);
    }
  }, [mode, selectedId, refetchServerMetrics]);

  // ── Compare loader (N-way, baseline = first non-empty slot) ──
  const compareSelectedIds = compareIds.filter(Boolean);
  const compareValid = compareSelectedIds.length >= 2
    && new Set(compareSelectedIds).size === compareSelectedIds.length;
  const runCompare = useCallback(async () => {
    if (!compareValid) return;
    setMode('compare'); setCompareData(null);
    try {
      const res = await api.compareBenchmarksN(compareSelectedIds);
      if (res.ok) setCompareData(res.data);
      else setErr(res.error || 'Compare failed');
    } catch (e) { setErr(e.message); }
  }, [compareValid, compareSelectedIds]);
  const setCompareSlot = (idx, value) => {
    setCompareIds((prev) => {
      const next = [...prev];
      next[idx] = value || null;
      return next;
    });
  };
  const addCompareSlot = () => {
    setCompareIds((prev) => prev.length >= 6 ? prev : [...prev, null]);
  };
  const removeCompareSlot = (idx) => {
    setCompareIds((prev) => prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx));
  };

  // ── Export ──
  const doExport = async (id, format) => {
    setExporting(true);
    try {
      await api.exportBenchmark(id, format);
    } catch (e) {
      setErr(`Export failed: ${e.message}`);
    } finally { setExporting(false); }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: S[500] }}>
        <RefreshCw size={20} style={{ animation: 'wfiSpin 1s linear infinite' }} /> Loading benchmark runs…
      </div>
    );
  }

  if (err) {
    return (
      <div style={{
        padding: '12px 16px', borderRadius: 8, background: '#fef2f2',
        border: '1px solid #fecaca', color: '#991b1b', fontSize: 13,
      }}>
        <AlertTriangle size={14} /> {err}
        <button onClick={() => { setErr(null); loadRuns(); }} style={{
          marginLeft: 12, padding: '3px 10px', fontSize: 11, borderRadius: 4,
          border: '1px solid #fecaca', background: '#fff', cursor: 'pointer',
        }}>Retry</button>
      </div>
    );
  }

  // ── COMPARE MODE ──
  if (mode === 'compare') {
    return (
      <BenchmarkCompareView
        runs={runs}
        ids={compareSelectedIds}
        data={compareData}
        onBack={() => { setMode('list'); setCompareData(null); }}
      />
    );
  }

  // ── DETAIL MODE ──
  if (mode === 'detail' && selectedId) {
    return (
      <BenchmarkDetailView
        run={detail}
        serverPerf={serverPerf}
        tailSec={tailSec}
        onTailSecChange={handleTailSecChange}
        onBack={() => { setMode('list'); setSelectedId(null); setDetail(null); setServerPerf(null); }}
        onExport={(fmt) => doExport(selectedId, fmt)}
        exporting={exporting}
      />
    );
  }

  // ── LIST MODE (default) ──
  return (
    <div>
      {/* N-way compare picker — up to 6 runs side-by-side. Slot 0 is the
          baseline (other slots show Δ vs it). Operators can compare 3, 4, 5
          stress-test reruns to see if a fix actually held across attempts. */}
      <div style={{
        padding: '14px 16px', borderRadius: 10, marginBottom: 16,
        border: `1px solid ${S[200]}`, background: S[50],
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: S[700] }}>
            Compare runs (baseline + up to 5 reruns)
          </div>
          <span style={{ fontSize: 11, color: S[500] }}>
            · first slot = baseline, deltas computed vs it
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {compareIds.map((id, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {idx > 0 && <span style={{ fontSize: 11, color: S[400] }}>vs</span>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: idx === 0 ? CHART.primary : S[500],
                  textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {idx === 0 ? 'Baseline' : `Rerun ${idx}`}
                </span>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <select value={id || ''}
                    onChange={(e) => setCompareSlot(idx, e.target.value || null)}
                    style={{ ...pickerStyle, minWidth: 220,
                      borderColor: idx === 0 ? CHART.primary : S[200] }}>
                    <option value="">Pick run…</option>
                    {runs.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name} · {shortTime(r.startedAt)}
                      </option>
                    ))}
                  </select>
                  {compareIds.length > 2 && (
                    <button onClick={() => removeCompareSlot(idx)}
                      title="Remove this slot"
                      style={{
                        padding: '4px 8px', fontSize: 11, borderRadius: 4,
                        border: `1px solid ${S[200]}`, background: '#fff', color: S[500],
                        cursor: 'pointer',
                      }}>×</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {compareIds.length < 6 && (
            <button onClick={addCompareSlot}
              style={{
                alignSelf: 'flex-end', padding: '6px 12px', fontSize: 11, borderRadius: 6,
                border: `1px dashed ${S[300]}`, background: '#fff', color: S[600],
                cursor: 'pointer', fontWeight: 600,
              }}>
              + Add rerun
            </button>
          )}
          <button onClick={runCompare} disabled={!compareValid}
            style={{
              alignSelf: 'flex-end',
              padding: '7px 16px', fontSize: 12, fontWeight: 700, borderRadius: 8,
              border: 'none',
              background: !compareValid ? S[200] : BRAND.gradient,
              color: '#fff',
              cursor: !compareValid ? 'not-allowed' : 'pointer',
              boxShadow: !compareValid ? 'none' : '0 1px 3px rgba(226,74,58,0.30)',
            }}>
            Compare {compareSelectedIds.length || ''} runs →
          </button>
        </div>
      </div>

      {/* Window filter visibility — make it obvious why runs may be missing
          when nothing matches; also surface a "show all" escape hatch when
          we've filtered everything out but the unfiltered list has data. */}
      {allRuns.length > 0 && runs.length !== allRuns.length && (
        <div style={{
          fontSize: 11, color: S[600], marginBottom: 10,
          padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 8,
          background: ACCENT.indigo.bg, border: `1px solid ${ACCENT.indigo.border}`,
          borderRadius: 6, color: ACCENT.indigo.text,
        }}>
          Showing <strong>{runs.length}</strong> of {allRuns.length} runs ·
          window <strong>{windowLabel}</strong>
        </div>
      )}

      {/* Run table */}
      {runs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: S[500] }}>
          <Activity size={28} color={S[300]} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {allRuns.length === 0 ? 'No benchmark runs yet' : 'No runs in this window'}
          </div>
          <div style={{ fontSize: 12 }}>
            {allRuns.length === 0
              ? 'Run the loadtest harness — it auto-creates a benchmark record. See loadtest/loadtest_v2.py.'
              : `${allRuns.length} run${allRuns.length === 1 ? '' : 's'} exist outside the selected window — widen the time range to see them.`}
          </div>
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff', overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 100px 110px 100px 90px 90px 80px',
            padding: '10px 14px', background: S[50], borderBottom: `1px solid ${S[200]}`,
            fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            <div>Name</div><div>Status</div><div>Started</div>
            <div>Duration</div><div>Fired</div><div>Errors</div><div>Env</div>
          </div>
          {runs.map((r, i) => (
            <div key={r.id}
                 onClick={() => openDetail(r.id)}
                 style={{
                   display: 'grid', gridTemplateColumns: '2fr 100px 110px 100px 90px 90px 80px',
                   padding: '12px 14px',
                   borderBottom: i < runs.length - 1 ? `1px solid ${S[100]}` : 'none',
                   fontSize: 13, alignItems: 'center', cursor: 'pointer',
                 }}
                 onMouseEnter={e => e.currentTarget.style.background = S[50]}
                 onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div>
                <div style={{ fontWeight: 600, color: S[900] }}>{r.name}</div>
                {r.harnessVersion && (
                  <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>
                    {r.harnessVersion}
                  </div>
                )}
              </div>
              <div>
                <StatusBadge status={r.status} />
              </div>
              <div style={{ fontSize: 11, color: S[600] }}>{shortTime(r.startedAt)}</div>
              <div style={{ fontSize: 12, color: S[700] }}>
                {r.durationSec != null ? `${(Number(r.durationSec) / 60).toFixed(1)} min` : '—'}
              </div>
              <div style={{ fontSize: 12, color: S[700] }}>
                {(() => {
                  const t = r.aggregateMetrics && r.aggregateMetrics.totals;
                  return t ? Number(t.fired || 0).toLocaleString() : '—';
                })()}
              </div>
              <div style={{ fontSize: 12, color: S[700] }}>
                {(() => {
                  const t = r.aggregateMetrics && r.aggregateMetrics.totals;
                  if (!t) return '—';
                  const pct = Number(t.error_rate_pct || 0);
                  const color = pct > 5 ? '#dc2626' : pct > 1 ? '#d97706' : '#16a34a';
                  return <span style={{ color, fontWeight: 600 }}>{pct.toFixed(1)}%</span>;
                })()}
              </div>
              <div style={{ fontSize: 11, color: S[500] }}>{r.environment || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const pickerStyle = {
  fontSize: 12, padding: '5px 10px', borderRadius: 6,
  border: '1px solid #d6d3d1', background: '#fff',
  fontFamily: 'inherit', minWidth: 240,
};

function StatusBadge({ status }) {
  const colors = {
    RUNNING:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
    COMPLETED: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
    FAILED:    { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
    ABORTED:   { bg: '#f5f5f4', text: '#57534e', border: '#d6d3d1' },
  };
  const c = colors[status] || colors.ABORTED;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
    }}>{status || 'UNKNOWN'}</span>
  );
}

/* ─── ServerPerformancePanel ──────────────────────────────────── */
/* Renders the server-measured workflow performance for a benchmark
   run's window. Sourced from /api/v1/benchmarks/{id}/server-metrics
   which proxies WorkflowIntelligenceService.getPerformance().

   This panel exists because the existing per-stage table only shows
   the client-side numbers the harness POSTed in aggregateMetrics —
   useful for "what did the test machine experience?" but useless for
   "is the platform healthy?" The two answers diverge by 100x+ under
   saturation. We render both side-by-side so operators can immediately
   see when the script is self-throttling and the platform is fine.

   Renders nothing if serverPerf is null (loading or no startedAt).
*/
function ServerPerformancePanel({ serverPerf }) {
  if (!serverPerf) return null;

  // Shape mirrors WorkflowIntelligenceService.getPerformance:
  //   percentiles: { p50, p90, p95, p99, max, avg }   -- workflow latency
  //   taskMetrics: { tasksPerWorkflow:{avg,p99,max}, taskDurationMs:{...},
  //                  p99TaskBurstPerSec, tasksPerSec, totalInvocations }
  //   workflowsPerSec, tasksPerSec
  //   runCount, completedCount, inFlightCount
  //   successRate (0.0-1.0), errorRate
  // perf-debug 2026-05-02 (#131 follow-up): the client/server p99 ratio
  // panel that used to live here was removed when we ripped client-side
  // timing out of the UI. Operators now read the per-stage server table
  // and this overall server panel as ground truth — no need to compare
  // against numbers we no longer render.
  const p = serverPerf.percentiles || {};
  const tm = serverPerf.taskMetrics || {};
  const td = tm.taskDurationMs || {};
  const tpw = tm.tasksPerWorkflow || {};

  const fmt = (v, suffix = '') => {
    if (v == null) return '—';
    const n = Number(v);
    if (!isFinite(n)) return '—';
    return n >= 1000 ? `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`
                     : `${n.toFixed(n < 10 ? 2 : n < 100 ? 1 : 0)}${suffix}`;
  };
  const fmtPct = (frac) => frac == null ? '—' : `${(Number(frac) * 100).toFixed(2)}%`;
  const fmtMs = (v) => v == null ? '—' : `${Math.round(Number(v))}ms`;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 13, fontWeight: 700, color: S[700], marginBottom: 8,
      }}>
        <span>Server-side workflow performance</span>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 8px',
          borderRadius: 4, background: '#0d4f3c20', color: '#0d4f3c',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          ground truth
        </span>
      </div>

      <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff', overflow: 'hidden' }}>
        {/* Headline stat row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0,
          borderBottom: `1px solid ${S[100]}`,
        }}>
          {[
            { label: 'Workflows in window', value: fmt(serverPerf.runCount),
              sub: `${fmt(serverPerf.completedCount)} completed · ${fmt(serverPerf.inFlightCount)} in-flight` },
            { label: 'Success rate', value: fmtPct(serverPerf.successRate),
              sub: `${fmt(serverPerf.successCount)} ok · ${fmt(serverPerf.failedCount)} failed` },
            { label: 'Workflows / sec', value: fmt(serverPerf.workflowsPerSec, '/s'),
              sub: `${fmt(serverPerf.tasksPerSec, '/s')} tasks · ${fmt(tpw.avg)} tasks/wf avg` },
            { label: 'Burst ceiling', value: fmt(tm.p99TaskBurstPerSec, '/s'),
              sub: 'p99 task starts in any 1-sec slice' },
          ].map(s => (
            <div key={s.label} style={{
              padding: '12px 16px',
              borderRight: `1px solid ${S[100]}`,
            }}>
              <div style={{ fontSize: 10, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {s.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: S[900], marginTop: 2 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: S[500], marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Latency percentiles row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr',
          padding: '10px 16px',
          fontSize: 11, color: S[600],
          background: S[50], borderBottom: `1px solid ${S[100]}`,
          textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
        }}>
          <div></div>
          <div>p50</div><div>p90</div><div>p95</div><div>p99</div><div>max</div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr',
          padding: '10px 16px', fontSize: 13, alignItems: 'center',
          borderBottom: `1px solid ${S[100]}`,
        }}>
          <div style={{ fontWeight: 600, color: S[700] }}>Workflow latency</div>
          <div>{fmtMs(p.p50)}</div>
          <div>{fmtMs(p.p90)}</div>
          <div>{fmtMs(p.p95)}</div>
          <div style={{ fontWeight: 700 }}>{fmtMs(p.p99)}</div>
          <div style={{ color: S[500] }}>{fmtMs(p.max)}</div>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr 1fr 1fr 1fr 1fr',
          padding: '10px 16px', fontSize: 13, alignItems: 'center',
        }}>
          <div style={{ fontWeight: 600, color: S[700] }}>Per-task latency</div>
          <div>{fmtMs(td.p50)}</div>
          <div>{fmtMs(td.p90)}</div>
          <div>{fmtMs(td.p95)}</div>
          <div style={{ fontWeight: 700 }}>{fmtMs(td.p99)}</div>
          <div style={{ color: S[500] }}>{fmtMs(td.max)}</div>
        </div>

      </div>
    </div>
  );
}

/* ─── PerStageServerTable ─────────────────────────────────────── */
/* Renders one row per stage with SERVER-MEASURED latency for that
   stage's wall-clock window. Sourced from
   /api/v1/benchmarks/{id}/server-metrics → perStage[].
   Replaces the old client-side stages table that surfaced harness
   HTTP roundtrip timings (badly distorted under saturation by Python
   pool / asyncio queueing). Demand-side columns (target_rps, fired,
   errors) survive because they're honest measurements of what we
   tried to push through the API. */
function PerStageServerTable({ serverPerf, runStatus }) {
  const perStage = serverPerf && Array.isArray(serverPerf.perStage)
    ? serverPerf.perStage : [];

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: S[700], marginBottom: 8,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Per-stage results</span>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 8px',
          borderRadius: 4, background: '#0d4f3c20', color: '#0d4f3c',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>server-measured</span>
      </div>
      <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff', overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '50px 80px 80px 80px 90px 80px 70px 70px 70px',
          padding: '8px 14px', background: S[50], borderBottom: `1px solid ${S[200]}`,
          fontSize: 10, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Stage</div>
          <div>Target RPS</div>
          <div>Fired</div>
          <div>Errors</div>
          <div>Server WF count</div>
          <div>Success %</div>
          <div>p50 ms</div>
          <div>p95 ms</div>
          <div>p99 ms</div>
        </div>
        {perStage.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 12 }}>
            {runStatus === 'RUNNING'
              ? 'Per-stage server metrics will appear when stages complete.'
              : 'No per-stage server metrics. Profile may lack stages[]/duration_sec_total.'}
          </div>
        )}
        {perStage.map((s, i) => {
          const srv = s.server || {};
          const pct = srv.percentiles || {};
          const success = srv.successRate != null ? (Number(srv.successRate) * 100) : null;
          const successColor = success == null ? S[400]
            : success >= 99.9 ? '#16a34a'
            : success >= 95 ? '#d97706' : '#dc2626';
          return (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '50px 80px 80px 80px 90px 80px 70px 70px 70px',
              padding: '8px 14px',
              borderBottom: i < perStage.length - 1 ? `1px solid ${S[100]}` : 'none',
              fontSize: 12, alignItems: 'center',
            }}>
              <div style={{ fontWeight: 600, color: S[900] }}>{s.stage}</div>
              <div>{s.target_rps != null ? s.target_rps : '—'}</div>
              <div>{s.fired != null ? Number(s.fired).toLocaleString() : '—'}</div>
              <div>
                {s.errors != null && Number(s.errors) > 0
                  ? <span style={{ color: '#dc2626' }}>{s.errors}</span>
                  : (s.errors != null ? 0 : '—')}
              </div>
              <div>{srv.runCount != null ? Number(srv.runCount).toLocaleString() : '—'}</div>
              <div style={{ color: successColor, fontWeight: 600 }}>
                {success != null ? `${success.toFixed(2)}%` : '—'}
              </div>
              <div>{pct.p50 != null ? Math.round(pct.p50) : '—'}</div>
              <div>{pct.p95 != null ? Math.round(pct.p95) : '—'}</div>
              <div style={{ fontWeight: 600 }}>{pct.p99 != null ? Math.round(pct.p99) : '—'}</div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: S[500], marginTop: 6, fontStyle: 'italic' }}>
        ◀ Latency percentiles are server-measured
        (workflow_runs.completedAt − startedAt) for each stage's wall-clock
        window. Fired / Errors columns are demand-side from the harness:
        how many HTTP starts were attempted and how many returned 4xx/5xx.
      </div>
    </div>
  );
}

/* ─── BenchmarkDetailView ─────────────────────────────────────── */
function BenchmarkDetailView({ run, serverPerf, tailSec = 60, onTailSecChange, onBack, onExport, exporting }) {
  if (!run) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: S[500] }}>
        <RefreshCw size={20} style={{ animation: 'wfiSpin 1s linear infinite' }} /> Loading…
      </div>
    );
  }
  // perf-debug 2026-05-02 (#131 follow-up): we no longer pull
  // aggregateMetrics.stages here — the per-stage rendering now reads
  // server-measured percentiles via serverPerf.perStage. Demand totals
  // (fired/succeeded/errors/error_rate_pct) are still useful as
  // demand-side metrics so we keep `totals`.
  const totals = (run.aggregateMetrics && run.aggregateMetrics.totals) || {};
  const snapshots = Array.isArray(run.snapshots) ? run.snapshots : [];

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16,
        padding: '14px 16px', borderRadius: 10,
        border: `1px solid ${S[200]}`, background: S[50],
      }}>
        <button onClick={onBack} style={{
          fontSize: 12, padding: '4px 10px', borderRadius: 6,
          border: `1px solid ${S[200]}`, background: '#fff', color: S[600], cursor: 'pointer',
        }}>← Back</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>{run.name}</div>
          <div style={{ fontSize: 12, color: S[600], marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span><StatusBadge status={run.status} /></span>
            <span>id <code style={{ fontFamily: 'ui-monospace, monospace' }}>{String(run.id).slice(0, 13)}…</code></span>
            <span>started {shortTime(run.startedAt)}</span>
            {run.durationSec != null && <span>duration {(Number(run.durationSec) / 60).toFixed(1)} min</span>}
            {run.environment && <span>env <strong>{run.environment}</strong></span>}
            {run.gitSha && <span>sha <code>{String(run.gitSha).slice(0, 8)}</code></span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onExport('pdf')} disabled={exporting}
            title="Branded PDF report — cover, executive summary, per-stage table, snapshot timeline, latency distribution, hot-spots, protection"
            style={exportBtnPdf}>
            <FileText size={13} />
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
          <button onClick={() => onExport('xlsx')} disabled={exporting}
            title="Multi-sheet workbook — Summary, Stages, Snapshots, Latency, Percentiles, Hot-Spots, Protection"
            style={exportBtnXlsx}>
            <Database size={13} />
            {exporting ? 'Exporting…' : 'Export XLSX'}
          </button>
        </div>
      </div>

      {/* Totals headline */}
      {totals.fired != null && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16,
        }}>
          {[
            { label: 'Fired', v: Number(totals.fired || 0).toLocaleString() },
            { label: 'Succeeded', v: Number(totals.succeeded || 0).toLocaleString() },
            { label: 'Errors', v: Number(totals.errors || 0).toLocaleString() },
            { label: 'Error rate', v: `${Number(totals.error_rate_pct || 0).toFixed(2)}%` },
          ].map(s => (
            <div key={s.label} style={{
              padding: '14px 16px', borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff',
            }}>
              <div style={{ fontSize: 11, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {s.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: S[900], marginTop: 4 }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tail-buffer toggle — perf-debug 2026-05-02 follow-up.
          The default 60-second tail catches workflows that fired in the
          last second of the run but completed shortly after the harness
          stopped, giving the latency aggregation more honest tail
          samples. Operators who want the sustained-rate calculation to
          match k6's own reported request rate exactly need the
          firing-window-only view (tailSec=0) — same workflow count, but
          divided by the harness's wall-clock duration with no buffer.
          This toggle re-fetches the server-metrics window from the
          backend. The PerStage table and ServerPerformancePanel below
          both re-render on the new data. */}
      {run.startedAt && onTailSecChange && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 10, marginBottom: 12,
          border: `1px solid ${S[200]}`, background: S[50],
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: S[700],
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Server-metrics window
          </span>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
            border: `1px solid ${S[200]}`, background: '#fff' }}>
            <button
              type="button"
              onClick={() => onTailSecChange(60)}
              style={{
                padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                border: 'none',
                background: tailSec >= 60 ? CHART.primary : '#fff',
                color: tailSec >= 60 ? '#fff' : S[700],
                fontWeight: tailSec >= 60 ? 600 : 500,
              }}>
              With tail (+60s)
            </button>
            <button
              type="button"
              onClick={() => onTailSecChange(0)}
              style={{
                padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                border: 'none', borderLeft: `1px solid ${S[200]}`,
                background: tailSec === 0 ? CHART.primary : '#fff',
                color: tailSec === 0 ? '#fff' : S[700],
                fontWeight: tailSec === 0 ? 600 : 500,
              }}>
              Firing window only
            </button>
          </div>
          <span style={{ fontSize: 11, color: S[500], fontStyle: 'italic', flex: 1, minWidth: 240 }}>
            {tailSec === 0
              ? 'Window = startedAt → completedAt. Sustained rate matches the harness exactly; tail-completing workflows are excluded from latency percentiles.'
              : 'Window = startedAt → completedAt + 60s. Includes workflows that finished shortly after the harness stopped firing — better latency aggregation, slightly diluted rate.'}
          </span>
        </div>
      )}

      {/* Per-stage table — SERVER-SIDE metrics only.
          Perf-debug 2026-05-02 (#131 follow-up): we used to render the
          loadtest harness's own p50/p95/p99 here. Those numbers are
          HTTP-request-roundtrip latency from the script's perspective
          and get badly distorted under saturation by Python connection
          pool, asyncio scheduler, and nginx queueing — they don't
          reflect platform health. We now pull server-measured per-stage
          performance via /api/v1/benchmarks/{id}/server-metrics
          (which slices workflow_runs.completedAt-startedAt by each
          stage's wall-clock window) and render THOSE percentiles.
          The client-side fired/errors columns survive because they are
          honest "demand" numbers (how much did the script attempt to
          push?), not latency. */}
      <PerStageServerTable serverPerf={serverPerf} runStatus={run.status} />


      {/* Server-side performance panel — perf-debug 2026-05-02 #131.
          Sourced from WorkflowIntelligenceService.getPerformance() for
          the run's wall-clock window. Independent of client-side
          aggregateMetrics; tells us what the platform actually did. */}
      <ServerPerformancePanel serverPerf={serverPerf} />

      {/* Snapshot timeline — periodic capture from the harness during
          firing. We render only the demand-side fields here (runs/in-
          flight/ok/throughput) — the latency columns that used to be
          here were the harness's own client-side p50/p95/p99 which
          gets distorted under saturation (see PerStageServerTable
          above for honest server-side latency per stage). */}
      {snapshots.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: S[700], marginBottom: 8 }}>
            Snapshot timeline ({snapshots.length} captured)
          </div>
          <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff',
                        overflow: 'auto', maxHeight: 400 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '90px 100px 100px 100px 130px',
              padding: '8px 14px', background: S[50], borderBottom: `1px solid ${S[200]}`,
              fontSize: 10, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
              position: 'sticky', top: 0,
            }}>
              <div>Elapsed</div><div>Runs Started</div><div>In-Flight</div><div>OK</div>
              <div>Throughput / hr</div>
            </div>
            {snapshots.map((sn, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '90px 100px 100px 100px 130px',
                padding: '6px 14px', borderBottom: i < snapshots.length - 1 ? `1px solid ${S[100]}` : 'none',
                fontSize: 11, alignItems: 'center',
              }}>
                <div>{Number(sn.elapsed_sec || 0).toFixed(0)}s</div>
                <div>{Number(sn.runs_total || 0).toLocaleString()}</div>
                <div>{Number(sn.in_flight || 0).toLocaleString()}</div>
                <div>{Number(sn.ok_count || 0).toLocaleString()}</div>
                <div>{Number(sn.throughput_per_hr || 0).toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {run.notes && (
        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 8,
                      background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Notes
          </div>
          <div style={{ fontSize: 12, color: S[700], whiteSpace: 'pre-wrap' }}>{run.notes}</div>
        </div>
      )}
    </div>
  );
}

// Themed export buttons — PDF uses brand orange (because it's the
// "headline" deliverable), XLSX uses indigo (matches CHART.primary so the
// data-deck association is obvious). Both share the same surface shape so
// they read as a pair.
const exportBtnPdf = {
  fontSize: 12, padding: '7px 14px', borderRadius: 8, fontWeight: 700,
  border: 'none', background: BRAND.gradient, color: '#fff', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  boxShadow: '0 1px 3px rgba(226,74,58,0.30)',
  fontFamily: 'inherit',
};
const exportBtnXlsx = {
  fontSize: 12, padding: '7px 14px', borderRadius: 8, fontWeight: 700,
  border: `1px solid ${CHART.primary}`, background: '#fff', color: CHART.primary,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontFamily: 'inherit',
};

/* ─── BenchmarkCompareView (N-way) ──────────────────────────────
   Renders a wide matrix: one column per run (baseline first), one row per
   stage. Cells show p99 / p50 / errors with the baseline numbers in the
   first column and Δ-vs-baseline in subsequent columns, color-coded for
   "got faster" (green) / "got slower" (red).
*/
function BenchmarkCompareView({ runs, ids, data, onBack }) {
  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: S[500] }}>
        <RefreshCw size={20} style={{ animation: 'wfiSpin 1s linear infinite' }} /> Computing diff…
      </div>
    );
  }
  const selectedRuns = ids.map((id) => runs.find((r) => r.id === id) || { id, name: '—' });
  const stageMatrix = data.stages || [];
  const totalsMatrix = data.totals || [];
  const colCount = ids.length;

  // Render one Δ cell with consistent semantics: positive = got slower (red),
  // negative = got faster (green), abs<5% of baseline reads as neutral so
  // tiny noise doesn't paint everything alarmist.
  const deltaCell = (deltaMs, baselineMs) => {
    if (deltaMs == null) return '—';
    const pct = baselineMs ? (deltaMs / baselineMs) * 100 : 0;
    const neutral = Math.abs(pct) < 5;
    const color = neutral ? S[500] : (deltaMs > 0 ? '#dc2626' : '#059669');
    const sign = deltaMs > 0 ? '+' : '';
    const fmtPct = neutral ? '' : ` (${sign}${pct.toFixed(1)}%)`;
    return (
      <span style={{ fontWeight: 700, color, fontFamily: "'IBM Plex Mono', monospace" }}>
        {sign}{Math.round(deltaMs)} ms{fmtPct}
      </span>
    );
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16,
        padding: '14px 16px', borderRadius: 10,
        border: `1px solid ${S[200]}`, background: S[50],
      }}>
        <button onClick={onBack} style={{
          fontSize: 12, padding: '4px 10px', borderRadius: 6,
          border: `1px solid ${S[200]}`, background: '#fff', color: S[600], cursor: 'pointer',
        }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>
            {colCount}-way comparison · baseline {selectedRuns[0]?.name || '—'}
          </div>
          <div style={{ fontSize: 12, color: S[600], marginTop: 4, lineHeight: 1.6 }}>
            Δ columns are computed against the baseline run (first column).
            <span style={{ color: '#dc2626', fontWeight: 600 }}> Red = slower</span> ·
            <span style={{ color: '#059669', fontWeight: 600 }}> Green = faster</span> ·
            grey = within ±5% of baseline.
          </div>
        </div>
      </div>

      {/* Run header strip — one card per run with name + total stats so the
          operator always sees what the columns mean before reading numbers. */}
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        gap: 8, marginBottom: 16,
      }}>
        {totalsMatrix.map((t, i) => {
          const isBase = i === 0;
          const errDelta = t.error_rate_delta_vs_baseline_pct;
          return (
            <div key={t.runId} style={{
              padding: '12px 14px', borderRadius: 10,
              background: '#fff',
              border: `1px solid ${isBase ? CHART.primary : S[200]}`,
              borderTop: `3px solid ${isBase ? CHART.primary : S[400]}`,
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: isBase ? CHART.primary : S[500],
                textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {isBase ? 'Baseline' : `Rerun ${i}`}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: S[900],
                marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.name || '—'}
              </div>
              <div style={{ fontSize: 11, color: S[600], marginTop: 6, lineHeight: 1.5 }}>
                fired <strong>{Number(t.fired || 0).toLocaleString()}</strong> ·
                err rate <strong>{Number(t.error_rate_pct || 0).toFixed(2)}%</strong>
                {!isBase && errDelta != null && (
                  <div style={{ fontSize: 10, marginTop: 4,
                    color: Math.abs(errDelta) < 0.5 ? S[500] : (errDelta > 0 ? '#dc2626' : '#059669'),
                    fontWeight: 700 }}>
                    Δ err {errDelta > 0 ? '+' : ''}{errDelta.toFixed(2)} pp vs baseline
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Stage matrix — one column per run, one row per stage. P99 is the
          headline since that's what stress-test reruns are comparing. */}
      <div style={{ borderRadius: 10, border: `1px solid ${S[200]}`,
        background: '#fff', overflow: 'auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `90px repeat(${colCount}, minmax(180px, 1fr))`,
          padding: '10px 14px', background: S[50],
          borderBottom: `1px solid ${S[200]}`,
          fontSize: 10, fontWeight: 700, color: S[600],
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          <div>Stage · target RPS</div>
          {selectedRuns.map((r, i) => (
            <div key={r.id} style={{ color: i === 0 ? CHART.primary : S[700] }}>
              {i === 0 ? '★ Baseline' : `Run ${i + 1}`}
            </div>
          ))}
        </div>
        {stageMatrix.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 12 }}>
            No comparable stages yet — baseline run has no aggregate_metrics.
          </div>
        )}
        {stageMatrix.map((row, rIdx) => {
          // perf-debug 2026-05-02 (#131 follow-up): client-side p50/p99
          // and the p99 delta vs baseline used to render here. They were
          // ripped out when we removed harness HTTP-roundtrip latency
          // from the UI — those numbers are dominated by client pool/
          // asyncio queueing under saturation, not platform performance.
          // We now show demand-side fields only (target RPS, actual fire
          // rate, fired, errors). For latency comparison across runs,
          // open each run's detail (where the per-stage server-measured
          // percentiles are rendered) — a future enhancement should
          // surface server-side per-stage deltas in this matrix too.
          return (
            <div key={rIdx} style={{
              display: 'grid',
              gridTemplateColumns: `90px repeat(${colCount}, minmax(180px, 1fr))`,
              padding: '12px 14px',
              borderBottom: rIdx < stageMatrix.length - 1 ? `1px solid ${S[100]}` : 'none',
              fontSize: 12, alignItems: 'center',
            }}>
              <div style={{ fontWeight: 700, color: S[800] }}>
                Stage {row.stage}
                <div style={{ fontSize: 10, color: S[500], marginTop: 2, fontWeight: 500 }}>
                  {row.target_rps} RPS
                </div>
              </div>
              {(row.perRun || []).map((cell, cIdx) => {
                if (cell.missing) {
                  return <div key={cIdx} style={{ color: S[400] }}>—</div>;
                }
                return (
                  <div key={cIdx} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: S[800] }}>
                      <strong>{Number(cell.fired || 0).toLocaleString()}</strong>
                      <span style={{ color: S[500], fontWeight: 500 }}> fired</span>
                    </div>
                    <div style={{ fontSize: 11, color: S[600],
                      fontFamily: "'IBM Plex Mono', monospace" }}>
                      {Number(cell.actual_rps || 0).toFixed(1)} actual RPS
                    </div>
                    {Number(cell.errors || 0) > 0 && (
                      <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>
                        {cell.errors} errors
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
